import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Building2, Plus, Trash2, Mail, X,
  Users, BarChart3, LogOut, MessageCircle, Upload,
  Database, RefreshCw, Loader2, Save, Search, ChevronDown, ChevronUp,
  FolderOpen, CheckCircle2, Clock, Phone, AlertTriangle, Download,
  FileSpreadsheet, FileText, Link, Activity, CreditCard, Settings, Globe, Server, Layers, Wallet,
  Pause, Play, Square, Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { BarChart as RBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { API, apiCall, formatDate } from "@/lib/api";
import { AdminProvidersTab } from "@/components/tabs/AdminProvidersTab";
import { ApiUsageTab } from "@/components/tabs/ApiUsageTab";
import { ThemeToggle } from "@/components/ThemeToggle";

const ExportButton = ({ filterType, count, label, icon: Icon, colorClass, onExport }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button className={colorClass} disabled={count === 0} size="sm">
        <Icon className="w-3 h-3 mr-1" />{label} ({count})
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
      <DropdownMenuItem onClick={() => onExport(filterType, "xlsx")} className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer">
        <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onExport(filterType, "csv")} className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer">
        <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onExport(filterType, "txt")} className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer">
        <FileText className="w-4 h-4 mr-2" /> TXT
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const RapidApiUsageBadge = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const { ok, data } = await apiCall("GET", `${API}/admin/rapidapi-usage`);
      if (!alive) return;
      if (ok) setData(data);
    };
    load();
    const t = setInterval(load, 60000); // refresh every 60s
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data?.providers?.length) return null;

  // Show only enabled providers, sorted so C (main) is first
  const items = data.providers.filter(p => p.enabled).sort((a, b) => (a.provider === "C" ? -1 : 1));
  if (!items.length) return null;

  const fmt = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  return (
    <div className="hidden md:flex items-center gap-2" data-testid="rapidapi-usage-badge">
      {items.map(p => {
        const pct = p.pct;
        const critical = pct >= 90;
        const warn = pct >= 70 && pct < 90;
        const barColor = critical ? "bg-rose-500" : warn ? "bg-amber-500" : "bg-emerald-500";
        const textColor = critical ? "text-rose-300" : warn ? "text-amber-300" : "text-emerald-300";
        const bgColor = critical ? "bg-rose-500/10 border-rose-500/30" : warn ? "bg-amber-500/10 border-amber-500/30" : "bg-emerald-500/10 border-emerald-500/20";
        return (
          <div
            key={p.provider}
            className={`px-3 py-1.5 rounded-lg border text-xs ${bgColor}`}
            title={`${p.name}: ${p.used.toLocaleString("es-AR")} de ${p.quota.toLocaleString("es-AR")} requests este mes`}
            data-testid={`rapidapi-usage-${p.provider}`}
          >
            <div className={`flex items-center gap-2 ${textColor}`}>
              <span className="font-semibold uppercase tracking-wide">API {p.provider}</span>
              <span className="font-mono">{fmt(p.used)} / {fmt(p.quota)}</span>
              <span className="font-bold">{pct.toFixed(1)}%</span>
            </div>
            <div className="mt-1 h-1 w-full rounded bg-black/30 overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("companies");

  const handleLogout = async () => {
    try {
      await apiCall("POST", `${API}/auth/logout`);
      navigate("/", { replace: true });
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <header className="glass fixed top-0 left-0 right-0 z-50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md overflow-hidden">
              <img src="/veritel-logo.jpeg" alt="Veritel" className="w-full h-full" />
            </div>
            <span className="font-black text-xl text-white tracking-tight">Veritel</span>
            <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20">Admin</Badge>
          </div>
          <div className="flex items-center gap-4">
            <RapidApiUsageBadge />
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="go-dashboard-btn">
              <FolderOpen className="w-4 h-4 mr-2" />Dashboard
            </Button>
            <div className="flex items-center gap-3 pl-4 border-l border-zinc-800">
              {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
              <span className="text-sm text-zinc-300 hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-2">Administracion</h1>
            <p className="text-zinc-400">Gestiona empresas, creditos, grupos de WhatsApp y campanas.</p>
          </div>

          <Tabs value={activeTab} className="space-y-6" onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1 flex-wrap">
              <TabsTrigger value="companies" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-companies-tab">
                <Building2 className="w-4 h-4 mr-2" />Empresas
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-users-tab">
                <Users className="w-4 h-4 mr-2" />Usuarios
              </TabsTrigger>
              <TabsTrigger value="groups" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-groups-tab">
                <Link className="w-4 h-4 mr-2" />Grupos WA
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-campaigns-tab">
                <FolderOpen className="w-4 h-4 mr-2" />Campanas
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-whatsapp-tab">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp Cache
              </TabsTrigger>
              <TabsTrigger value="activity" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-activity-tab">
                <Activity className="w-4 h-4 mr-2" />Actividad
              </TabsTrigger>
              <TabsTrigger value="stats" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-stats-tab">
                <BarChart3 className="w-4 h-4 mr-2" />Estadisticas
              </TabsTrigger>
              <TabsTrigger value="enacom" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-enacom-tab">
                <Database className="w-4 h-4 mr-2" />ENACOM
              </TabsTrigger>
              <TabsTrigger value="globalwpp" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-globalwpp-tab">
                <MessageCircle className="w-4 h-4 mr-2" />Campañas WA
              </TabsTrigger>
              <TabsTrigger value="nicar" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-nicar-tab">
                <Globe className="w-4 h-4 mr-2" />NIC.ar
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-settings-tab">
                <Settings className="w-4 h-4 mr-2" />Configuracion
              </TabsTrigger>
              <TabsTrigger value="providers" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-providers-tab">
                <Server className="w-4 h-4 mr-2" />Proveedores API
              </TabsTrigger>
              <TabsTrigger value="api-usage" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-zinc-400" data-testid="admin-api-usage-tab">
                <Activity className="w-4 h-4 mr-2" />Uso API
              </TabsTrigger>
            </TabsList>

            <TabsContent value="companies"><CompaniesTab /></TabsContent>
            <TabsContent value="users"><AdminUsersTab /></TabsContent>
            <TabsContent value="groups"><GroupsTab /></TabsContent>
            <TabsContent value="campaigns"><AdminCampaignsTab /></TabsContent>
            <TabsContent value="whatsapp"><AdminWhatsAppTab /></TabsContent>
            <TabsContent value="activity"><ActivityLogTab /></TabsContent>
            <TabsContent value="stats"><StatsTab /></TabsContent>
            <TabsContent value="enacom"><AdminEnacomTab /></TabsContent>
            <TabsContent value="globalwpp"><AdminGlobalWPPTab /></TabsContent>
            <TabsContent value="nicar"><AdminNicArTab /></TabsContent>
            <TabsContent value="settings"><PlatformSettingsTab /></TabsContent>
            <TabsContent value="providers"><AdminProvidersTab /></TabsContent>
            <TabsContent value="api-usage"><ApiUsageTab /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

// ==================== COMPANIES TAB ====================
const CompaniesTab = () => {
  const [companies, setCompanies] = useState([]);
  const [newName, setNewName] = useState("");
  const [newEmails, setNewEmails] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmails, setEditEmails] = useState("");
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditsEdit, setCreditsEdit] = useState(null);
  const [creditsPhone, setCreditsPhone] = useState(0);
  const [creditsEmail, setCreditsEmail] = useState(0);
  const [creditsWhatsapp, setCreditsWhatsapp] = useState(0);
  // Services config
  const [servicesEdit, setServicesEdit] = useState(null);
  const [svcPhone, setSvcPhone] = useState(true);
  const [svcEmail, setSvcEmail] = useState(true);
  const [svcWaEnabled, setSvcWaEnabled] = useState(false);
  const [svcWaProvider, setSvcWaProvider] = useState("rapidapi_v2");
  const [svcWaC, setSvcWaC] = useState(true);
  const [svcWaCache, setSvcWaCache] = useState(true);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [apiTestLoading, setApiTestLoading] = useState(false);
  const [svcMaxCampaigns, setSvcMaxCampaigns] = useState(10);
  const [savingServices, setSavingServices] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/companies`);
      if (ok) setCompanies(data.companies || []);
    } catch { console.error("Error fetching companies"); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return toast.error("Ingresa el nombre de la empresa");
    const emails = newEmails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
    setLoading(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/companies`, { name: newName.trim(), emails });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success(`Empresa "${data.name}" creada`);
      setNewName("");
      setNewEmails("");
      fetchCompanies();
    } catch (e) {
      toast.error(e.message || "Error al crear empresa");
    } finally { setLoading(false); }
  };

  const handleUpdate = async () => {
    if (!editingCompany) return;
    const emails = editEmails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/companies/${editingCompany.company_id}`, { name: editName.trim(), emails });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Empresa actualizada");
      setEditingCompany(null);
      fetchCompanies();
    } catch (e) { toast.error(e.message || "Error al actualizar"); }
  };

  const handleDelete = async (companyId) => {
    try {
      await apiCall("DELETE", `${API}/admin/companies/${companyId}`);
      toast.success("Empresa eliminada");
      setCompanyToDelete(null);
      fetchCompanies();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleToggle = async (company) => {
    try {
      await apiCall("PUT", `${API}/admin/companies/${company.company_id}`, { is_active: !company.is_active });
      toast.success(company.is_active ? "Empresa desactivada" : "Empresa activada");
      fetchCompanies();
    } catch { toast.error("Error al actualizar"); }
  };

  const startEdit = (company) => {
    setEditingCompany(company);
    setEditName(company.name);
    setEditEmails(company.emails.join(", "));
  };

  const startCreditsEdit = (company) => {
    setCreditsEdit(company);
    setCreditsPhone(company.credits_phone || 0);
    setCreditsEmail(company.credits_email || 0);
    setCreditsWhatsapp(company.credits_whatsapp || 0);
  };

  const [balanceEdit, setBalanceEdit] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");

  const handleAdjustBalance = async () => {
    if (!balanceEdit || !balanceAmount) return;
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/company/${balanceEdit.company_id}/balance`, {
        amount: parseFloat(balanceAmount),
        reason: balanceReason || "Ajuste manual",
      });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success(data.message);
      setBalanceEdit(null); setBalanceAmount(""); setBalanceReason("");
      fetchCompanies();
    } catch (e) { toast.error(e.message || "Error"); }
  };

  const handleSaveCredits = async () => {
    if (!creditsEdit) return;
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/companies/${creditsEdit.company_id}/credits`, {
        credits_phone: creditsPhone,
        credits_email: creditsEmail,
        credits_whatsapp: creditsWhatsapp
      });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Creditos actualizados");
      setCreditsEdit(null);
      fetchCompanies();
    } catch (e) { toast.error(e.message || "Error al actualizar creditos"); }
  };

  const startServicesEdit = (company) => {
    const svc = company.services || { phone_validation: true, email_validation: true, whatsapp_validation: { enabled: false, provider: "rapidapi" } };
    setServicesEdit(company);
    setSvcPhone(svc.phone_validation !== false);
    setSvcEmail(svc.email_validation !== false);
    setSvcWaEnabled(svc.whatsapp_validation?.enabled || false);
    const prov = svc.whatsapp_validation?.provider || "rapidapi_v2";
    setSvcWaProvider(prov);
    setSvcWaC(prov === "rapidapi_v2" || prov === "rapidapi_both" || prov === "rapidapi");
    setSvcWaCache(svc.whatsapp_validation?.use_cache !== false);
    setSvcMaxCampaigns(svc.max_campaigns ?? 50);
  };

  const handleSaveServices = async () => {
    if (!servicesEdit) return;
    setSavingServices(true);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/companies/${servicesEdit.company_id}/services`, {
        services: {
          phone_validation: svcPhone,
          email_validation: svcEmail,
          whatsapp_validation: {
            enabled: svcWaEnabled,
            provider: svcWaC ? "rapidapi_v2" : svcWaProvider,
            use_cache: svcWaCache
          },
          max_campaigns: svcMaxCampaigns
        }
      });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Servicios actualizados");
      setServicesEdit(null);
      fetchCompanies();
    } catch (e) { toast.error(e.message || "Error al actualizar servicios"); }
    finally { setSavingServices(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-rose-400" />Nueva Empresa
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Nombre</Label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ej: RECUPERO CREDITICIO" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-company-name" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Emails (separados por coma)</Label>
            <Input value={newEmails} onChange={e => setNewEmails(e.target.value)} placeholder="email1@empresa.com, email2@empresa.com" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-company-emails" />
          </div>
        </div>
        <Button onClick={handleCreate} disabled={loading || !newName.trim()} className="mt-4 bg-rose-600 hover:bg-rose-700 text-white" data-testid="create-company-btn">
          <Building2 className="w-4 h-4 mr-2" />Crear Empresa
        </Button>
      </div>

      <div className="space-y-3">
        {companies.length === 0 ? (
          <div className="text-center py-12 text-zinc-500"><Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay empresas creadas</p></div>
        ) : (
          companies.map(company => (
            <div key={company.company_id} className="bg-zinc-900/50 border border-white/10 rounded-lg p-5 backdrop-blur-sm" data-testid={`company-${company.company_id}`}>
              {editingCompany?.company_id === company.company_id ? (
                <div className="space-y-3">
                  <Input value={editName} onChange={e => setEditName(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white" />
                  <Input value={editEmails} onChange={e => setEditEmails(e.target.value)} placeholder="Emails separados por coma" className="bg-zinc-950 border-zinc-800 text-white" />
                  <div className="flex gap-2">
                    <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm"><Save className="w-3 h-3 mr-1" />Guardar</Button>
                    <Button onClick={() => setEditingCompany(null)} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" size="sm">Cancelar</Button>
                  </div>
                </div>
              ) : creditsEdit?.company_id === company.company_id ? (
                <div className="space-y-3">
                  <h3 className="font-bold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-400" />Creditos: {company.name}</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1"><Phone className="w-3 h-3" />Telefono</Label>
                      <Input type="number" value={creditsPhone} onChange={e => setCreditsPhone(parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" />Email</Label>
                      <Input type="number" value={creditsEmail} onChange={e => setCreditsEmail(parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1"><MessageCircle className="w-3 h-3" />WhatsApp</Label>
                      <Input type="number" value={creditsWhatsapp} onChange={e => setCreditsWhatsapp(parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 text-white" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveCredits} className="bg-amber-600 hover:bg-amber-700 text-white" size="sm"><Save className="w-3 h-3 mr-1" />Guardar Creditos</Button>
                    <Button onClick={() => setCreditsEdit(null)} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" size="sm">Cancelar</Button>
                  </div>
                </div>
              ) : balanceEdit?.company_id === company.company_id ? (
                <div className="space-y-4" data-testid={`balance-edit-${company.company_id}`}>
                  <h3 className="font-bold text-white flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" />Ajustar Saldo: {company.name}</h3>
                  <p className="text-sm text-zinc-400">Saldo actual: <span className="text-amber-400 font-bold">${(company.balance_ars || 0).toLocaleString("es-AR")}</span></p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400 mb-1 block">Monto (+ agregar, - quitar)</Label>
                      <Input type="number" value={balanceAmount} onChange={e => setBalanceAmount(e.target.value)} placeholder="5000" className="bg-zinc-950 border-zinc-800 text-white" data-testid="balance-amount-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400 mb-1 block">Motivo</Label>
                      <Input value={balanceReason} onChange={e => setBalanceReason(e.target.value)} placeholder="Bonificacion, ajuste..." className="bg-zinc-950 border-zinc-800 text-white" data-testid="balance-reason-input" />
                    </div>
                  </div>
                  {balanceAmount && <p className="text-xs text-zinc-500">Nuevo saldo: <span className="text-white font-semibold">${((company.balance_ars || 0) + (parseFloat(balanceAmount) || 0)).toLocaleString("es-AR")}</span></p>}
                  <div className="flex gap-2">
                    <Button onClick={handleAdjustBalance} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="save-balance-btn"><Save className="w-3 h-3 mr-1" />Aplicar Ajuste</Button>
                    <Button onClick={() => setBalanceEdit(null)} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" size="sm">Cancelar</Button>
                  </div>
                </div>
              ) : servicesEdit?.company_id === company.company_id ? (
                <div className="space-y-4" data-testid={`services-edit-${company.company_id}`}>
                  <h3 className="font-bold text-white flex items-center gap-2"><Settings className="w-4 h-4 text-cyan-400" />Servicios: {company.name}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-violet-400" />
                        <span className="text-sm text-zinc-300">Verificacion de telefonos</span>
                      </div>
                      <Switch checked={svcPhone} onCheckedChange={setSvcPhone} data-testid="svc-phone-toggle" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <span className="text-sm text-zinc-300">Verificacion de emails</span>
                      </div>
                      <Switch checked={svcEmail} onCheckedChange={setSvcEmail} data-testid="svc-email-toggle" />
                    </div>
                    <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-green-400" />
                          <span className="text-sm text-zinc-300">Verificacion de WhatsApp</span>
                        </div>
                        <Switch checked={svcWaEnabled} onCheckedChange={setSvcWaEnabled} data-testid="svc-wa-toggle" />
                      </div>
                      {svcWaEnabled && (
                        <div className="pl-6 space-y-3 border-l-2 border-green-500/20 ml-2">
                          <div>
                            <Label className="text-xs text-zinc-400 mb-1.5 block">Proveedor</Label>
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={svcWaC} onChange={e => { setSvcWaC(e.target.checked); if (e.target.checked) setSvcWaProvider("rapidapi_v2"); }} className="accent-green-500 w-4 h-4" data-testid="svc-provider-v2" />
                                <span className="text-xs text-white">Verificador C <span className="text-green-400">(RapidAPI v2 - 125 nums/seg)</span></span>
                              </label>
                              <Button
                                size="sm"
                                className="mt-2 bg-violet-600 hover:bg-violet-500 text-white text-xs h-7 px-3"
                                disabled={apiTestLoading}
                                data-testid="test-wa-api-inline"
                                onClick={async () => {
                                  setApiTestLoading(true);
                                  setApiTestResult(null);
                                  try {
                                    const { ok, data } = await apiCall("POST", `${API}/admin/test-wa-api`);
                                    if (ok) setApiTestResult(data);
                                    else setApiTestResult({ error: data?.detail || "Error" });
                                  } catch { setApiTestResult({ error: "Error de conexion" }); }
                                  finally { setApiTestLoading(false); }
                                }}
                              >
                                {apiTestLoading ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Testeando...</> : <><Activity className="w-3 h-3 mr-1" />Test APIs</>}
                              </Button>
                              {apiTestResult && (
                                <div className="mt-2 p-2 bg-zinc-900 rounded border border-zinc-700 text-xs space-y-1">
                                  {apiTestResult.error ? (
                                    <p className="text-rose-400">{apiTestResult.error}</p>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="text-zinc-400 w-12">API C:</span>
                                        {apiTestResult.api_c?.status === "OK" ? (
                                          <span className="text-green-400">OK - {apiTestResult.api_c.time_ms}ms</span>
                                        ) : (
                                          <span className="text-rose-400">ERROR: {apiTestResult.api_c?.error || "?"}</span>
                                        )}
                                      </div>
                                      {!apiTestResult.rapidapi_key_set && <p className="text-rose-400 font-bold">RAPIDAPI KEY NO CONFIGURADA</p>}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {(svcWaC) && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-zinc-400">Usar cache de WhatsApp</span>
                                <Switch checked={svcWaCache} onCheckedChange={setSvcWaCache} data-testid="svc-wa-cache-toggle" />
                              </div>
                              {!svcWaCache && <p className="text-[10px] text-amber-400">Cache desactivado: siempre verificara con la API (mas lento, mas consumo)</p>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-zinc-300">Limite de campañas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" value={svcMaxCampaigns} onChange={e => setSvcMaxCampaigns(parseInt(e.target.value) || 0)}
                        className="w-16 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-center text-sm" data-testid="svc-max-campaigns" />
                      <span className="text-xs text-zinc-500">0 = ilimitado</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveServices} disabled={savingServices} className="bg-cyan-600 hover:bg-cyan-700 text-white" size="sm" data-testid="save-services-btn">
                      <Save className="w-3 h-3 mr-1" />{savingServices ? "Guardando..." : "Guardar Servicios"}
                    </Button>
                    <Button onClick={() => setServicesEdit(null)} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" size="sm">Cancelar</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-rose-400" />
                      <h3 className="font-bold text-white text-lg">{company.name}</h3>
                      <Badge className={company.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>
                        {company.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => startServicesEdit(company)} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" data-testid={`services-${company.company_id}`}>
                        <Settings className="w-4 h-4 mr-1" />Servicios
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => startCreditsEdit(company)} className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid={`credits-${company.company_id}`}>
                        <CreditCard className="w-4 h-4 mr-1" />Creditos
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setBalanceEdit(company); setBalanceAmount(""); setBalanceReason(""); }} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" data-testid={`balance-${company.company_id}`}>
                        <Wallet className="w-4 h-4 mr-1" />Saldo
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(company)} className="text-zinc-400 hover:text-white">Editar</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(company)} className="text-zinc-400 hover:text-amber-400">
                        {company.is_active ? "Desactivar" : "Activar"}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setCompanyToDelete(company)} className="text-zinc-500 hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(company.emails || []).map((email, i) => (
                      <Badge key={i} className="bg-zinc-800 text-zinc-300 border-zinc-700"><Mail className="w-3 h-3 mr-1" />{email}</Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-amber-400 font-semibold"><Wallet className="w-3 h-3" />${(company.balance_ars || 0).toLocaleString("es-AR")}</span>
                    {company.plan_name && <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-[10px]">{company.plan_name}</Badge>}
                    <span className="flex items-center gap-1 text-violet-400"><Phone className="w-3 h-3" />{company.credits_phone || 0} tel</span>
                    <span className="flex items-center gap-1 text-green-400"><MessageCircle className="w-3 h-3" />{company.credits_whatsapp || 0} WA</span>
                    <span className="text-xs text-zinc-600 ml-auto">{formatDate(company.created_at)}</span>
                  </div>
                  {company.services && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`services-badges-${company.company_id}`}>
                      <Badge className={company.services.phone_validation !== false ? "bg-violet-500/10 text-violet-400 border-violet-500/20 text-[10px]" : "bg-zinc-800 text-zinc-600 border-zinc-700 text-[10px]"}>
                        Tel {company.services.phone_validation !== false ? "ON" : "OFF"}
                      </Badge>
                      <Badge className={company.services.email_validation !== false ? "bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]" : "bg-zinc-800 text-zinc-600 border-zinc-700 text-[10px]"}>
                        Email {company.services.email_validation !== false ? "ON" : "OFF"}
                      </Badge>
                      <Badge className={company.services.whatsapp_validation?.enabled ? "bg-green-500/10 text-green-400 border-green-500/20 text-[10px]" : "bg-zinc-800 text-zinc-600 border-zinc-700 text-[10px]"}>
                        WA {company.services.whatsapp_validation?.enabled ? `ON (${
                          company.services.whatsapp_validation.provider === "rapidapi_v2" ? "C" :
                          company.services.whatsapp_validation.provider === "rapidapi_both" ? "C" :
                          company.services.whatsapp_validation.provider === "rapidapi" ? "C" : "-"
                        })` : "OFF"}
                      </Badge>
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!companyToDelete} onOpenChange={() => setCompanyToDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Se eliminara "{companyToDelete?.name}".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(companyToDelete?.company_id)} className="bg-rose-600 hover:bg-rose-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== GROUPS TAB ====================
const GroupsTab = () => {
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newGroupJid, setNewGroupJid] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupCompany, setNewGroupCompany] = useState("");
  const [newGroupMode, setNewGroupMode] = useState("phone");
  const [groupToDelete, setGroupToDelete] = useState(null);

  // UltraMsg config
  const [umInstance, setUmInstance] = useState("");
  const [umToken, setUmToken] = useState("");
  const [umConfigured, setUmConfigured] = useState(false);
  const [umSaving, setUmSaving] = useState(false);
  
  // UltraMsg messages
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [completionMsg, setCompletionMsg] = useState("");
  const [msgVarsWelcome, setMsgVarsWelcome] = useState([]);
  const [msgVarsCompletion, setMsgVarsCompletion] = useState([]);
  const [savingMsgs, setSavingMsgs] = useState(false);
  
  // UltraMsg remote groups
  const [remoteGroups, setRemoteGroups] = useState([]);
  const [loadingRemote, setLoadingRemote] = useState(false);

  // UltraMsg instances
  const [instances, setInstances] = useState([]);
  const [newInstId, setNewInstId] = useState("");
  const [newInstToken, setNewInstToken] = useState("");
  const [newInstName, setNewInstName] = useState("");
  const [selectedInstance, setSelectedInstance] = useState("");
  const [exportConfigGroup, setExportConfigGroup] = useState(null);
  const [exportTypes, setExportTypes] = useState(["all"]);
  const [savingExport, setSavingExport] = useState(false);
  const [modeConfigGroup, setModeConfigGroup] = useState(null);
  const [groupCampaignMode, setGroupCampaignMode] = useState("phone");
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchCompanies();
    fetchUmConfig();
    fetchUmMessages();
    fetchInstances();
  }, []);

  const fetchUmConfig = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/ultramsg/config`);
      if (ok) {
        setUmInstance(data.instance || "");
        setUmToken(data.token_set ? data.token : "");
        setUmConfigured(data.configured);
      }
    } catch {}
  };

  const handleSaveUmConfig = async () => {
    if (!umInstance.trim() || !umToken.trim()) return toast.error("Instance y Token son requeridos");
    setUmSaving(true);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/ultramsg/config`, { instance: umInstance.trim(), token: umToken.trim() });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Configuracion UltraMsg actualizada");
      setUmConfigured(true);
      fetchUmConfig();
    } catch (e) { toast.error(e.message); }
    finally { setUmSaving(false); }
  };

  const fetchUmMessages = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/ultramsg/messages`);
      if (ok) {
        setWelcomeMsg(data.welcome_message || "");
        setCompletionMsg(data.completion_message || "");
        setMsgVarsWelcome(data.variables_welcome || []);
        setMsgVarsCompletion(data.variables_completion || []);
      }
    } catch {}
  };

  const handleSaveMessages = async () => {
    setSavingMsgs(true);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/ultramsg/messages`, { welcome_message: welcomeMsg, completion_message: completionMsg });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Mensajes actualizados");
    } catch (e) { toast.error(e.message); }
    finally { setSavingMsgs(false); }
  };

  const fetchRemoteGroups = async () => {
    setLoadingRemote(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/ultramsg/groups${selectedInstance ? `?instance_id=${selectedInstance}` : ""}`);
      if (!ok) throw new Error(data.detail || "Error");
      setRemoteGroups(data.groups || []);
    } catch (e) { toast.error(e.message || "Error al obtener grupos"); }
    finally { setLoadingRemote(false); }
  };

  const fetchInstances = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/ultramsg/instances`);
      if (ok) setInstances(data.instances || []);
    } catch {}
  };

  const handleAddInstance = async () => {
    if (!newInstId.trim() || !newInstToken.trim()) return toast.error("Instance ID y Token son requeridos");
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/ultramsg/instances`, { instance_id: newInstId.trim(), token: newInstToken.trim(), name: newInstName.trim() || newInstId.trim() });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Instancia agregada");
      setNewInstId(""); setNewInstToken(""); setNewInstName("");
      fetchInstances();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteInstance = async (iid) => {
    try {
      const { ok, data } = await apiCall("DELETE", `${API}/admin/ultramsg/instances/${iid}`);
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Instancia eliminada");
      fetchInstances();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleInstance = async (iid, active) => {
    try {
      await apiCall("PUT", `${API}/admin/ultramsg/instances/${iid}`, { is_active: active });
      fetchInstances();
    } catch {}
  };

  const handleSaveExportConfig = async () => {
    if (!exportConfigGroup) return;
    setSavingExport(true);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/groups/${encodeURIComponent(exportConfigGroup)}/export-config`, { export_types: exportTypes });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Configuracion de exportacion guardada");
      setExportConfigGroup(null);
      fetchGroups();
    } catch (e) { toast.error(e.message); }
    finally { setSavingExport(false); }
  };

  const handleSaveCampaignMode = async () => {
    if (!modeConfigGroup) return;
    setSavingMode(true);
    try {
      const { ok, data } = await apiCall("PUT", `${API}/admin/groups/${encodeURIComponent(modeConfigGroup)}/campaign-mode`, { campaign_mode: groupCampaignMode });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Modo de verificacion actualizado");
      setModeConfigGroup(null);
      fetchGroups();
    } catch (e) { toast.error(e.message); }
    finally { setSavingMode(false); }
  };

  const handleLinkRemoteGroup = async (group) => {
    if (!newGroupCompany) return toast.error("Selecciona una empresa primero");
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/groups`, {
        group_jid: group.group_jid,
        company_id: newGroupCompany,
        group_name: group.name,
        campaign_mode: newGroupMode
      });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success(`Grupo "${group.name}" vinculado`);
      fetchGroups();
      fetchRemoteGroups();
    } catch (e) { toast.error(e.message); }
  };

  const fetchGroups = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/groups`);
      if (ok) setGroups(data.groups || []);
    } catch {}
  };

  const fetchCompanies = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/companies`);
      if (ok) setCompanies(data.companies || []);
    } catch {}
  };

  const handleCreate = async () => {
    if (!newGroupJid.trim() || !newGroupCompany) return toast.error("Completa todos los campos");
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/groups`, {
        group_jid: newGroupJid.trim(),
        company_id: newGroupCompany,
        group_name: newGroupName.trim(),
        campaign_mode: newGroupMode
      });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("Grupo vinculado");
      setNewGroupJid("");
      setNewGroupName("");
      setNewGroupCompany("");
      setNewGroupMode("phone");
      fetchGroups();
    } catch (e) { toast.error(e.message || "Error al vincular grupo"); }
  };

  const handleDelete = async (groupJid) => {
    try {
      await apiCall("DELETE", `${API}/admin/groups/${encodeURIComponent(groupJid)}`);
      toast.success("Grupo desvinculado");
      setGroupToDelete(null);
      fetchGroups();
    } catch { toast.error("Error al desvincular"); }
  };

  return (
    <div className="space-y-6">
      {/* UltraMsg Configuration */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Settings className="w-5 h-5 text-amber-400" />Configuracion UltraMsg
        </h2>
        <p className="text-sm text-zinc-400 mb-4">
          Configura la instancia de UltraMsg para recibir archivos por WhatsApp.
          Webhook URL: <code className="bg-zinc-800 px-2 py-1 rounded text-green-400 text-xs">https://veritel.com.ar/api/webhooks/ultramsg</code>
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Instance ID</Label>
            <Input value={umInstance} onChange={e => setUmInstance(e.target.value)} placeholder="instance33764" className="bg-zinc-950 border-zinc-800 text-white" data-testid="um-instance-input" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Token</Label>
            <Input value={umToken} onChange={e => setUmToken(e.target.value)} placeholder="Token de UltraMsg" className="bg-zinc-950 border-zinc-800 text-white" data-testid="um-token-input" type="password" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={handleSaveUmConfig} disabled={umSaving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-um-config-btn">
            <Save className="w-4 h-4 mr-2" />{umSaving ? "Guardando..." : "Guardar Config"}
          </Button>
          {umConfigured && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Configurado</Badge>}
        </div>
      </div>

      {/* Multiple Instances */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Server className="w-5 h-5 text-violet-400" />Instancias UltraMsg ({instances.length})
        </h2>
        <p className="text-sm text-zinc-400 mb-4">Gestiona multiples instancias de UltraMsg. Una instancia puede servir a varias empresas.</p>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <Input value={newInstId} onChange={e => setNewInstId(e.target.value)} placeholder="instance33764" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-inst-id" />
          <Input value={newInstToken} onChange={e => setNewInstToken(e.target.value)} placeholder="Token" type="password" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-inst-token" />
          <div className="flex gap-2">
            <Input value={newInstName} onChange={e => setNewInstName(e.target.value)} placeholder="Nombre (opcional)" className="bg-zinc-950 border-zinc-800 text-white flex-1" />
            <Button onClick={handleAddInstance} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="add-instance-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {instances.map(inst => (
            <div key={inst.instance_id} className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg" data-testid={`instance-${inst.instance_id}`}>
              <div className="flex items-center gap-3">
                <Badge className={inst.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>
                  {inst.is_active ? "Activa" : "Inactiva"}
                </Badge>
                <span className="font-mono text-white">{inst.instance_id}</span>
                <span className="text-sm text-zinc-400">{inst.name}</span>
                <code className="text-xs text-zinc-600">{inst.token_masked}</code>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={inst.is_active} onCheckedChange={v => handleToggleInstance(inst.instance_id, v)} />
                <Button variant="ghost" size="icon" onClick={() => handleDeleteInstance(inst.instance_id)} className="text-zinc-500 hover:text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Configurable Messages */}
      {umConfigured && (
        <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
          <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-400" />Mensajes Automaticos
          </h2>
          <p className="text-sm text-zinc-400 mb-4">Personaliza los mensajes que se envian automaticamente al grupo de WhatsApp.</p>
          
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-zinc-300 mb-1 block">Mensaje de Recepcion</Label>
              <p className="text-xs text-zinc-500 mb-2">Se envia cuando se recibe un archivo. Variables: {msgVarsWelcome.map(v => <code key={v} className="bg-zinc-800 px-1.5 py-0.5 rounded text-amber-400 text-xs mx-0.5">{v}</code>)}</p>
              <textarea value={welcomeMsg} onChange={e => setWelcomeMsg(e.target.value)} rows={4} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm font-mono resize-y" data-testid="welcome-msg-input" />
            </div>
            
            <div>
              <Label className="text-sm font-semibold text-zinc-300 mb-1 block">Mensaje de Finalizacion</Label>
              <p className="text-xs text-zinc-500 mb-2">Se envia como caption del archivo de resultados. Variables: {msgVarsCompletion.map(v => <code key={v} className="bg-zinc-800 px-1.5 py-0.5 rounded text-amber-400 text-xs mx-0.5">{v}</code>)}</p>
              <textarea value={completionMsg} onChange={e => setCompletionMsg(e.target.value)} rows={4} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm font-mono resize-y" data-testid="completion-msg-input" />
            </div>
            
            <Button onClick={handleSaveMessages} disabled={savingMsgs} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-messages-btn">
              <Save className="w-4 h-4 mr-2" />{savingMsgs ? "Guardando..." : "Guardar Mensajes"}
            </Button>
          </div>
        </div>
      )}

      {/* Discover Groups from UltraMsg */}
      {umConfigured && (
        <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-400" />Grupos Disponibles en WhatsApp
            </h2>
            <Button onClick={fetchRemoteGroups} disabled={loadingRemote} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="fetch-remote-groups-btn">
              {loadingRemote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Cargar Grupos
            </Button>
          </div>
          {remoteGroups.length > 0 ? (
            <div className="space-y-2">
              <div className="mb-3 flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Empresa destino para vincular:</Label>
                  <select value={newGroupCompany} onChange={e => setNewGroupCompany(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm" data-testid="link-group-company-select">
                    <option value="">Seleccionar empresa...</option>
                    {companies.filter(c => c.is_active).map(c => (
                      <option key={c.company_id} value={c.company_id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[200px]">
                  <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Modo de verificacion:</Label>
                  <select value={newGroupMode} onChange={e => setNewGroupMode(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm" data-testid="link-group-mode-select">
                    <option value="phone">Solo Telefono</option>
                    <option value="phone_whatsapp">Telefono + WhatsApp</option>
                    <option value="whatsapp_only">Solo WhatsApp</option>
                  </select>
                </div>
              </div>
              {remoteGroups.map(g => (
                <div key={g.group_jid} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <div>
                    <span className="font-medium text-white">{g.name}</span>
                    <span className="text-xs text-zinc-500 ml-3">{g.participants_count} miembros</span>
                    <code className="text-xs text-zinc-600 ml-3 font-mono">{g.group_jid}</code>
                  </div>
                  {g.is_linked ? (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Vinculado</Badge>
                  ) : (
                    <Button size="sm" onClick={() => handleLinkRemoteGroup(g)} disabled={!newGroupCompany} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`link-group-${g.group_jid}`}>
                      <Link className="w-3 h-3 mr-1" />Vincular
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm text-center py-4">Presiona "Cargar Grupos" para ver los grupos disponibles en tu instancia de UltraMsg</p>
          )}
        </div>
      )}

      {/* Manual Group Linking */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Link className="w-5 h-5 text-green-400" />Vincular Grupo Manualmente
        </h2>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Group JID</Label>
            <Input value={newGroupJid} onChange={e => setNewGroupJid(e.target.value)} placeholder="120363xxxxxx@g.us" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-group-jid" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Nombre del grupo</Label>
            <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Verificaciones RC" className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-group-name" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Empresa</Label>
            <select value={newGroupCompany} onChange={e => setNewGroupCompany(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm" data-testid="new-group-company">
              <option value="">Seleccionar...</option>
              {companies.filter(c => c.is_active).map(c => (
                <option key={c.company_id} value={c.company_id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Modo</Label>
            <select value={newGroupMode} onChange={e => setNewGroupMode(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm" data-testid="new-group-mode">
              <option value="phone">Solo Telefono</option>
              <option value="phone_whatsapp">Tel + WhatsApp</option>
              <option value="whatsapp_only">Solo WhatsApp</option>
            </select>
          </div>
        </div>
        <Button onClick={handleCreate} disabled={!newGroupJid.trim() || !newGroupCompany} className="mt-4 bg-green-600 hover:bg-green-700 text-white" data-testid="create-group-btn">
          <Link className="w-4 h-4 mr-2" />Vincular Grupo
        </Button>
      </div>

      <div className="space-y-3">
        {groups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500"><MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay grupos vinculados</p></div>
        ) : (
          groups.map(group => (
            <div key={group.group_jid} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4" data-testid={`group-${group.group_jid}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <MessageCircle className="w-4 h-4 text-green-400" />
                    <span className="font-medium text-white">{group.group_name}</span>
                    <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20">{group.company_name}</Badge>
                    <Badge className={group.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>
                      {group.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                    <Badge className={
                      (group.campaign_mode || "phone") === "phone" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
                      (group.campaign_mode || "phone") === "phone_whatsapp" ? "bg-violet-500/10 text-violet-400 border-violet-500/20" :
                      "bg-green-500/10 text-green-400 border-green-500/20"
                    } data-testid={`group-mode-badge-${group.group_jid}`}>
                      {(group.campaign_mode || "phone") === "phone" ? "Solo Tel" :
                       (group.campaign_mode || "phone") === "phone_whatsapp" ? "Tel+WA" : "Solo WA"}
                    </Badge>
                    {group.export_types && !group.export_types.includes("all") && (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                        Filtro: {group.export_types.join(", ")}
                      </Badge>
                    )}
                  </div>
                  <code className="text-xs text-zinc-500 font-mono">{group.group_jid}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setModeConfigGroup(group.group_jid); setGroupCampaignMode(group.campaign_mode || "phone"); }} className="text-zinc-400 hover:text-violet-400" data-testid={`mode-config-${group.group_jid}`}>
                    <Phone className="w-4 h-4 mr-1" />Modo
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setExportConfigGroup(group.group_jid); setExportTypes(group.export_types || ["all"]); }} className="text-zinc-400 hover:text-amber-400" data-testid={`export-config-${group.group_jid}`}>
                    <Settings className="w-4 h-4 mr-1" />Filtro
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setGroupToDelete(group)} className="text-zinc-500 hover:text-rose-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Export Config Dialog */}
      <AlertDialog open={!!exportConfigGroup} onOpenChange={() => setExportConfigGroup(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Configurar tipo de archivo de respuesta</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Selecciona que tipos de resultados incluir en el archivo que se envia al grupo de WhatsApp.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Tipo de numero</p>
            {[
              { value: "all", label: "Todo (sin filtro)" },
              { value: "celular", label: "Solo Celulares" },
              { value: "fijo", label: "Solo Fijos" },
              { value: "invalido", label: "Solo Invalidos" },
            ].map(opt => (
              <label key={opt.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportTypes.includes(opt.value)}
                  onChange={e => {
                    if (opt.value === "all") {
                      setExportTypes(e.target.checked ? ["all"] : []);
                    } else {
                      let next = exportTypes.filter(t => t !== "all");
                      if (e.target.checked) { next.push(opt.value); } else { next = next.filter(t => t !== opt.value); }
                      setExportTypes(next.length === 0 ? ["all"] : next);
                    }
                  }}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-violet-600"
                  data-testid={`export-type-${opt.value}`}
                />
                <span className="text-white text-sm">{opt.label}</span>
              </label>
            ))}
            <div className="border-t border-zinc-700 my-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Filtro WhatsApp</p>
            {[
              { value: "con_wa", label: "Con WhatsApp" },
              { value: "sin_wa", label: "Sin WhatsApp" },
            ].map(opt => (
              <label key={opt.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportTypes.includes(opt.value)}
                  onChange={e => {
                    let next = exportTypes.filter(t => t !== "all");
                    if (e.target.checked) { next.push(opt.value); } else { next = next.filter(t => t !== opt.value); }
                    setExportTypes(next.length === 0 ? ["all"] : next);
                  }}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-600"
                  data-testid={`export-type-${opt.value}`}
                />
                <span className="text-white text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveExportConfig} disabled={savingExport} className="bg-violet-600 hover:bg-violet-700 text-white">
              {savingExport ? "Guardando..." : "Guardar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Campaign Mode Config Dialog */}
      <AlertDialog open={!!modeConfigGroup} onOpenChange={() => setModeConfigGroup(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Modo de Verificacion del Grupo</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Selecciona que tipo de verificacion se realizara cuando este grupo envie un archivo.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-4">
            {[
              { value: "phone", label: "Solo Telefono", desc: "Clasifica en celular, fijo e invalido" },
              { value: "phone_whatsapp", label: "Telefono + WhatsApp", desc: "Clasifica y luego verifica WhatsApp automaticamente" },
              { value: "whatsapp_only", label: "Solo WhatsApp", desc: "Verifica WhatsApp directamente sin normalizar" },
            ].map(opt => (
              <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-all ${groupCampaignMode === opt.value ? "bg-violet-500/10 border-violet-500/30" : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"}`}>
                <input
                  type="radio"
                  name="campaign_mode"
                  value={opt.value}
                  checked={groupCampaignMode === opt.value}
                  onChange={() => setGroupCampaignMode(opt.value)}
                  className="w-4 h-4 text-violet-600"
                  data-testid={`mode-option-${opt.value}`}
                />
                <div>
                  <span className="text-white text-sm font-medium">{opt.label}</span>
                  <p className="text-xs text-zinc-500">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveCampaignMode} disabled={savingMode} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-mode-btn">
              {savingMode ? "Guardando..." : "Guardar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!groupToDelete} onOpenChange={() => setGroupToDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Desvincular grupo?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">El grupo "{groupToDelete?.group_name}" dejara de recibir archivos para "{groupToDelete?.company_name}".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(groupToDelete?.group_jid)} className="bg-rose-600 hover:bg-rose-700 text-white">Desvincular</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== ACTIVITY LOG TAB ====================
const ActivityLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(1); }, []);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/activity?limit=30&page=${p}`);
      if (ok) { setLogs(data.logs || []); setTotal(data.total || 0); setPage(p); }
    } catch {}
    finally { setLoading(false); }
  };

  const actionLabels = {
    campaign_created: { label: "Campana creada", color: "text-violet-400" },
    campaign_completed: { label: "Campana completada", color: "text-emerald-400" },
    credits_assigned: { label: "Creditos asignados", color: "text-amber-400" },
    phone_verified: { label: "Telefono verificado", color: "text-blue-400" },
    whatsapp_sent: { label: "WhatsApp enviado", color: "text-green-400" },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-rose-400" />Log de Actividad ({total})
        </h2>
        <Button variant="ghost" size="icon" onClick={() => fetchLogs(1)} className="text-zinc-400 hover:text-white">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-400" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-zinc-500"><Activity className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay actividad registrada</p></div>
      ) : (
        <>
          <div className="space-y-2">
            {logs.map((log, i) => {
              const actionInfo = actionLabels[log.action] || { label: log.action, color: "text-zinc-400" };
              return (
                <div key={log.log_id || i} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4" data-testid={`activity-${log.log_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${actionInfo.color}`}>{actionInfo.label}</span>
                      {log.company_name && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs">{log.company_name}</Badge>}
                      <Badge className="bg-zinc-800 text-zinc-400 text-xs">{log.source}</Badge>
                    </div>
                    <span className="text-xs text-zinc-600">{formatDate(log.timestamp)}</span>
                  </div>
                  <p className="text-sm text-zinc-300">{log.details}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                    {log.user_email && <span>Usuario: {log.user_email}</span>}
                    {log.group_jid && <span>Grupo: {log.group_jid}</span>}
                    {log.credits_used > 0 && <span className="text-amber-400">Creditos: -{log.credits_used} ({log.credit_type})</span>}
                    {log.campaign_id && <span>Campana: {log.campaign_id.slice(0, 8)}...</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {total > 30 && (
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchLogs(page - 1)} className="bg-zinc-900 border-zinc-700 text-zinc-300">Anterior</Button>
              <span className="text-xs text-zinc-500">Pag {page} de {Math.ceil(total / 30)}</span>
              <Button size="sm" variant="outline" disabled={page * 30 >= total} onClick={() => fetchLogs(page + 1)} className="bg-zinc-900 border-zinc-700 text-zinc-300">Siguiente</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== ADMIN CAMPAIGNS TAB ====================
const AdminCampaignsTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/campaigns`);
      if (ok) setCampaigns(data.campaigns || []);
    } catch {}
  };

  const loadDetails = async (campaign) => {
    if (campaign.status === "processing" || campaign.status === "uploading") return;
    setLoadingPreview(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/analyze/phones/${campaign.analysis_id}/status?filter_by=all&page=1&page_size=100`);
      if (ok) { setSelectedCampaign(data); setExpandedId(campaign.analysis_id); }
    } catch { toast.error("Error al cargar detalles"); }
    finally { setLoadingPreview(false); }
  };

  const handleForceReset = async (analysisId, name) => {
    if (!window.confirm(`Forzar reset de "${name}"? Esto la marcara como error y desbloqueara la cola.`)) return;
    setActionLoading(analysisId);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/campaigns/${analysisId}/force-reset`);
      if (ok) { toast.success(data.message); fetchCampaigns(); }
      else toast.error(data?.detail || "Error");
    } catch { toast.error("Error de conexion"); }
    finally { setActionLoading(null); }
  };

  const handleRetry = async (analysisId, name) => {
    if (!window.confirm(`Reintentar "${name}"? Se re-procesara desde cero.`)) return;
    setActionLoading(analysisId);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/campaigns/${analysisId}/retry`);
      if (ok) { toast.success(data.message); fetchCampaigns(); }
      else toast.error(data?.detail || "Error");
    } catch { toast.error("Error de conexion"); }
    finally { setActionLoading(null); }
  };

  const handleExport = (filterType, format) => {
    if (!selectedCampaign?.analysis_id) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `${API}/analyze/phones/${selectedCampaign.analysis_id}/export?filter_type=${filterType}&format=${format}`;
    document.body.appendChild(iframe);
    setTimeout(() => document.body.removeChild(iframe), 30000);
    toast.success("Descargando...");
  };

  const filtered = filterStatus === "all" ? campaigns : campaigns.filter(c => c.status === filterStatus);
  const stuckCount = campaigns.filter(c => (c.status === "processing" && (c.progress || 0) === 0) || c.status === "uploading").length;
  const processingCount = campaigns.filter(c => c.status === "processing").length;
  const queuedCount = campaigns.filter(c => c.status === "queued").length;
  const errorCount = campaigns.filter(c => c.status === "error").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2" data-testid="admin-campaigns-title">
          <FolderOpen className="w-5 h-5 text-rose-400" />Todas las Campanas ({campaigns.length})
        </h2>
        <Button variant="ghost" size="icon" onClick={fetchCampaigns} className="text-zinc-400 hover:text-white" data-testid="admin-campaigns-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Status summary + filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant={filterStatus === "all" ? "default" : "outline"} onClick={() => setFilterStatus("all")} className={filterStatus === "all" ? "bg-zinc-700" : "text-zinc-400 border-zinc-700"}>
          Todas ({campaigns.length})
        </Button>
        {processingCount > 0 && <Button size="sm" variant={filterStatus === "processing" ? "default" : "outline"} onClick={() => setFilterStatus("processing")} className={filterStatus === "processing" ? "bg-amber-600" : "text-amber-400 border-amber-500/30"}>
          <Clock className="w-3 h-3 mr-1" />Procesando ({processingCount})
        </Button>}
        {queuedCount > 0 && <Button size="sm" variant={filterStatus === "queued" ? "default" : "outline"} onClick={() => setFilterStatus("queued")} className={filterStatus === "queued" ? "bg-indigo-600" : "text-indigo-400 border-indigo-500/30"}>
          En cola ({queuedCount})
        </Button>}
        {errorCount > 0 && <Button size="sm" variant={filterStatus === "error" ? "default" : "outline"} onClick={() => setFilterStatus("error")} className={filterStatus === "error" ? "bg-rose-600" : "text-rose-400 border-rose-500/30"}>
          <AlertTriangle className="w-3 h-3 mr-1" />Errores ({errorCount})
        </Button>}
        {stuckCount > 0 && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 ml-auto" data-testid="stuck-alert">
          <AlertTriangle className="w-3 h-3 mr-1" />{stuckCount} trabada{stuckCount > 1 ? "s" : ""}
        </Badge>}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500"><FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay campanas</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(campaign => {
            const isStuck = (campaign.status === "processing" && (campaign.progress || 0) === 0) || campaign.status === "uploading";
            const isError = campaign.status === "error";
            const isProcessing = campaign.status === "processing";
            const isQueued = campaign.status === "queued";
            const isUploading = campaign.status === "uploading";
            return (
            <div key={campaign.analysis_id} className={`bg-zinc-900/50 border rounded-lg p-4 backdrop-blur-sm ${isStuck ? "border-red-500/30" : isError ? "border-rose-500/20" : "border-white/10"}`} data-testid={`admin-campaign-${campaign.analysis_id}`}>
              <div className="flex items-center justify-between cursor-pointer" onClick={() => expandedId === campaign.analysis_id ? setExpandedId(null) : loadDetails(campaign)}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-medium text-white">{campaign.campaign_name}</p>
                    <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs">{campaign.company_name || "Sin empresa"}</Badge>
                    {campaign.source === "ultramsg" && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">via WhatsApp</Badge>}
                    {campaign.campaign_mode && <Badge className="bg-zinc-700 text-zinc-300 text-xs">{campaign.campaign_mode === "phone" ? "Tel" : campaign.campaign_mode === "whatsapp_only" ? "WA" : "Tel+WA"}</Badge>}
                    {isUploading && <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20"><Upload className="w-3 h-3 mr-1 animate-pulse" />Subiendo</Badge>}
                    {isQueued && <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20"><Clock className="w-3 h-3 mr-1" />En cola</Badge>}
                    {isProcessing && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20"><Clock className="w-3 h-3 mr-1 animate-pulse" />{campaign.progress || 0}%</Badge>}
                    {isError && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20"><AlertTriangle className="w-3 h-3 mr-1" />Error</Badge>}
                    {!isProcessing && !isQueued && !isUploading && !isError && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20"><CheckCircle2 className="w-3 h-3 mr-1" />Listo</Badge>}
                    {isStuck && <Badge className="bg-red-600 text-white text-xs animate-pulse">TRABADA</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span>{formatDate(campaign.timestamp)}</span>
                    <span className="text-white">{(campaign.total_rows || campaign.summary?.total || 0).toLocaleString("es-AR")} nums</span>
                    <span className="text-emerald-400">{(campaign.summary?.celular || 0).toLocaleString("es-AR")} cel</span>
                    <span className="text-blue-400">{(campaign.summary?.fijo || 0).toLocaleString("es-AR")} fijos</span>
                    <span className="text-rose-400">{(campaign.summary?.invalido || 0).toLocaleString("es-AR")} inv</span>
                    {isError && campaign.error && <span className="text-rose-400 truncate max-w-[300px]">{campaign.error}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Admin action buttons */}
                  {(isStuck || isProcessing || isUploading || isQueued) && (
                    <Button
                      size="sm" variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => { e.stopPropagation(); handleForceReset(campaign.analysis_id, campaign.campaign_name); }}
                      disabled={actionLoading === campaign.analysis_id}
                      data-testid={`force-reset-${campaign.analysis_id}`}
                    >
                      {actionLoading === campaign.analysis_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3 mr-1" />}
                      Forzar reset
                    </Button>
                  )}
                  {isError && (
                    <Button
                      size="sm" variant="ghost"
                      className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                      onClick={(e) => { e.stopPropagation(); handleRetry(campaign.analysis_id, campaign.campaign_name); }}
                      disabled={actionLoading === campaign.analysis_id}
                      data-testid={`retry-${campaign.analysis_id}`}
                    >
                      {actionLoading === campaign.analysis_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Reintentar
                    </Button>
                  )}
                  {expandedId === campaign.analysis_id ? <ChevronUp className="w-5 h-5 text-zinc-500" /> : <ChevronDown className="w-5 h-5 text-zinc-500" />}
                </div>
              </div>
              {expandedId === campaign.analysis_id && selectedCampaign && (
                <div className="mt-4 pt-4 border-t border-zinc-800 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 text-center"><p className="text-xl font-bold text-white">{selectedCampaign.summary?.total || 0}</p><p className="text-xs text-zinc-500">Total</p></div>
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center"><p className="text-xl font-bold text-emerald-400">{selectedCampaign.summary?.celular || 0}</p><p className="text-xs text-emerald-400/60">Celulares</p></div>
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center"><p className="text-xl font-bold text-blue-400">{selectedCampaign.summary?.fijo || 0}</p><p className="text-xs text-blue-400/60">Fijos</p></div>
                    <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-center"><p className="text-xl font-bold text-rose-400">{selectedCampaign.summary?.invalido || 0}</p><p className="text-xs text-rose-400/60">Invalidos</p></div>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center"><p className="text-xl font-bold text-amber-400">{selectedCampaign.summary?.normalizados || 0}</p><p className="text-xs text-amber-400/60">Norm.</p></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ExportButton filterType="all" count={selectedCampaign.summary?.total || 0} label="Todos" icon={Download} colorClass="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" onExport={handleExport} />
                    <ExportButton filterType="celular" count={selectedCampaign.summary?.celular || 0} label="Celulares" icon={Phone} colorClass="bg-emerald-600 hover:bg-emerald-700 text-white" onExport={handleExport} />
                    <ExportButton filterType="fijo" count={selectedCampaign.summary?.fijo || 0} label="Fijos" icon={Phone} colorClass="bg-blue-600 hover:bg-blue-700 text-white" onExport={handleExport} />
                    <ExportButton filterType="invalido" count={selectedCampaign.summary?.invalido || 0} label="Invalidos" icon={AlertTriangle} colorClass="bg-rose-600 hover:bg-rose-700 text-white" onExport={handleExport} />
                  </div>
                  {loadingPreview && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-rose-400" /></div>}
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN WHATSAPP TAB ====================
const AdminWhatsAppTab = () => {
  const [cache, setCache] = useState({ results: [], total: 0, total_pages: 1, verified_by_values: [] });
  const [cacheSearch, setCacheSearch] = useState("");
  const [cacheFilter, setCacheFilter] = useState("all");
  const [cacheVerifiedBy, setCacheVerifiedBy] = useState("");
  const [cacheDateFrom, setCacheDateFrom] = useState("");
  const [cacheDateTo, setCacheDateTo] = useState("");
  const [cachePage, setCachePage] = useState(1);
  const [stats, setStats] = useState(null);
  const [importingCsv, setImportingCsv] = useState(false);
  const [gwppBalance, setGwppBalance] = useState(null);
  const [editingPhone, setEditingPhone] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [reverifying, setReverifying] = useState(false);
  const [reverifyProvider, setReverifyProvider] = useState("rapidapi_v2");
  const [cleanupStats, setCleanupStats] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);

  useEffect(() => { loadModule(); loadCache("", "all", "", 1); }, []);

  const loadModule = async () => {
    try {
      const statsRes = await apiCall("GET", `${API}/whatsapp/stats`);
      if (statsRes.ok) setStats(statsRes.data);
    } catch {}
  };

  const handleCleanupCount = async () => {
    setCleaningUp(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/whatsapp-cache/cleanup-false-no`, { mode: "count" });
      if (ok) setCleanupStats(data);
      else toast.error(data?.detail || "Error");
    } catch { toast.error("Error de conexion"); }
    finally { setCleaningUp(false); }
  };

  const handleCleanupDeleteNo = async () => {
    if (!window.confirm("Esto eliminara TODOS los registros 'NO' del cache. Estos numeros se re-verificaran en la proxima campana. Continuar?")) return;
    setCleaningUp(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/whatsapp-cache/cleanup-false-no`, { mode: "delete_all_no" });
      if (ok) {
        toast.success(data.message);
        setCleanupStats(data);
        loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage);
      } else toast.error(data?.detail || "Error");
    } catch { toast.error("Error de conexion"); }
    finally { setCleaningUp(false); }
  };

  const handleCleanupDeleteErrors = async () => {
    setCleaningUp(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/whatsapp-cache/cleanup-false-no`, { mode: "delete_errors" });
      if (ok) {
        toast.success(data.message);
        setCleanupStats(data);
        loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage);
      } else toast.error(data?.detail || "Error");
    } catch { toast.error("Error de conexion"); }
    finally { setCleaningUp(false); }
  };

  const loadCache = async (search = cacheSearch, filter = cacheFilter, vby = cacheVerifiedBy, page = 1, dfrom = cacheDateFrom, dto = cacheDateTo) => {
    try {
      let url = `${API}/admin/whatsapp-cache?q=${encodeURIComponent(search)}&filter_by=${filter}&page=${page}&page_size=50`;
      if (vby) url += `&verified_by=${encodeURIComponent(vby)}`;
      if (dfrom) url += `&date_from=${dfrom}`;
      if (dto) url += `&date_to=${dto}`;
      const { ok, data } = await apiCall("GET", url);
      if (ok) { setCache(data); setCachePage(page); }
    } catch {}
  };

  const handleToggleStatus = async (phone, currentStatus) => {
    const newStatus = currentStatus === "SI" ? "NO" : "SI";
    setEditingPhone(phone);
    try {
      const { ok } = await apiCall("PUT", `${API}/admin/whatsapp-cache/${encodeURIComponent(phone)}`, { tiene_whatsapp: newStatus });
      if (ok) {
        toast.success(`${phone}: ${currentStatus} → ${newStatus}`);
        loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage, cacheDateFrom, cacheDateTo);
        loadModule();
      } else {
        toast.error("Error al actualizar");
      }
    } catch { toast.error("Error al actualizar"); }
    finally { setEditingPhone(null); }
  };

  const handleDelete = async (phone) => {
    try {
      const { ok } = await apiCall("DELETE", `${API}/admin/whatsapp-cache/${encodeURIComponent(phone)}`);
      if (ok) {
        toast.success(`${phone} eliminado del cache`);
        setDeleteConfirm(null);
        loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage, cacheDateFrom, cacheDateTo);
        loadModule();
      } else {
        toast.error("Error al eliminar");
      }
    } catch { toast.error("Error al eliminar"); }
  };

  const handleReverify = async () => {
    if (!window.confirm(`Re-verificar ${cache.total > 5000 ? "5000 (max)" : cache.total} numeros del filtro actual con API C?`)) return;
    setReverifying(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/whatsapp-cache/reverify`, {
        filter_status: cacheFilter === "all" ? "" : cacheFilter === "si" ? "SI" : cacheFilter === "no" ? "NO" : cacheFilter === "error" ? "ERROR" : "",
        filter_provider: cacheVerifiedBy,
        date_from: cacheDateFrom,
        date_to: cacheDateTo,
        api_provider: reverifyProvider,
        max_numbers: 5000
      });
      if (ok) {
        toast.success(`Re-verificacion iniciada: ${data.total_numbers} numeros`);
        // Poll for completion
        const pollId = setInterval(async () => {
          try {
            const res = await apiCall("GET", `${API}/admin/whatsapp-cache?page=1&page_size=1`);
            if (res.ok) {
              // Check if re-verify campaign completed
              loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo);
              loadModule();
            }
          } catch {}
        }, 10000);
        setTimeout(() => { clearInterval(pollId); setReverifying(false); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo); loadModule(); }, 120000);
      } else {
        toast.error(data?.detail || "Error al re-verificar");
        setReverifying(false);
      }
    } catch { toast.error("Error al re-verificar"); setReverifying(false); }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingCsv(true);
    const toastId = toast.loading("Subiendo archivo...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { ok, data } = await apiCall("POST", `${API}/whatsapp/import`, formData);
      if (!ok) throw new Error(data.detail || "Error");
      toast.dismiss(toastId);
      toast.success(`${data.total_parsed} numeros (${data.valido} SI, ${data.invalido} NO). Procesando...`);
      loadModule(); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo);
      const pollId = setInterval(async () => {
        try {
          const res = await apiCall("GET", `${API}/whatsapp/campaigns`);
          if (res.ok) {
            const camp = (res.data.campaigns || []).find(c => c.campaign_id === data.campaign_id);
            if (camp && camp.status === "completed") { clearInterval(pollId); toast.success("Importacion completada"); loadModule(); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo); }
            else if (camp && camp.status === "error") { clearInterval(pollId); toast.error("Error procesando"); loadModule(); }
          }
        } catch {}
      }, 5000);
      setTimeout(() => clearInterval(pollId), 300000);
    } catch (error) { toast.dismiss(toastId); toast.error(error.message || "Error al importar"); }
    finally { setImportingCsv(false); e.target.value = ""; }
  };

  const fetchBalance = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/globalwpp/balance`);
      if (ok) setGwppBalance(data.balance);
    } catch {}
  };

  const totalPages = cache.total_pages || Math.ceil(cache.total / 50) || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-400" />WhatsApp Cache</h2>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={fetchBalance} className="text-zinc-400 hover:text-white text-xs">{gwppBalance !== null ? `Saldo: ${gwppBalance}` : "Ver saldo"}</Button>
          <input type="file" id="admin-wpp-import" accept=".csv" onChange={handleImportCsv} className="hidden" />
          <label htmlFor="admin-wpp-import">
            <Button asChild disabled={importingCsv} className="bg-green-600 hover:bg-green-700 text-white cursor-pointer">
              <span>{importingCsv ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />...</> : <><Upload className="w-4 h-4 mr-2" />Importar CSV</>}</span>
            </Button>
          </label>
        </div>
      </div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-white">{stats.active_in_cache}</p><p className="text-xs text-zinc-500">En cache</p></div>
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-green-400">{stats.si_count}</p><p className="text-xs text-green-400/60">Tiene WA</p></div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-zinc-400">{stats.no_count}</p><p className="text-xs text-zinc-500">No tiene</p></div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-white">{stats.total_campaigns}</p><p className="text-xs text-zinc-500">Campanas WPP</p></div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-center" data-testid="admin-cache-days"><p className="text-2xl font-bold text-white">{stats.cache_days || 60}</p><p className="text-xs text-zinc-500">Días cache</p></div>
        </div>
      )}
      {stats && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="cache-export-bar">
          <span className="text-xs text-zinc-500 font-medium">Exportar TXT:</span>
          <Button size="sm" data-testid="export-cache-si-txt" onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/cache/export?filter_by=si&format=txt`; document.body.appendChild(iframe); setTimeout(() => document.body.removeChild(iframe), 30000); toast.success("Descargando números con WhatsApp..."); }} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 gap-1.5">
            <Download className="w-3.5 h-3.5" />Con WA ({stats.si_count})
          </Button>
          <Button size="sm" data-testid="export-cache-no-txt" onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/cache/export?filter_by=no&format=txt`; document.body.appendChild(iframe); setTimeout(() => document.body.removeChild(iframe), 30000); toast.success("Descargando números sin WhatsApp..."); }} className="bg-zinc-600 hover:bg-zinc-500 text-white text-xs h-8 gap-1.5">
            <Download className="w-3.5 h-3.5" />Sin WA ({stats.no_count})
          </Button>
          <Button size="sm" data-testid="export-cache-all-txt" onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/cache/export?filter_by=all&format=txt`; document.body.appendChild(iframe); setTimeout(() => document.body.removeChild(iframe), 30000); toast.success("Descargando todos los números..."); }} variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-xs h-8 gap-1.5">
            <Download className="w-3.5 h-3.5" />Todos ({stats.active_in_cache})
          </Button>
        </div>
      )}
      {/* Cleanup section */}
      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-lg p-4" data-testid="cache-cleanup-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-amber-400 mb-1">Limpieza de cache - Falsos "NO"</h3>
            <p className="text-xs text-zinc-500">Elimina registros "NO" que pueden ser falsos (de errores de API). Se re-verificaran en la proxima campana.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleCleanupCount} disabled={cleaningUp} className="bg-zinc-700 hover:bg-zinc-600 text-white h-8 text-xs" data-testid="cache-cleanup-count-btn">
              {cleaningUp ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              Diagnosticar
            </Button>
            <Button size="sm" onClick={handleCleanupDeleteNo} disabled={cleaningUp} className="bg-amber-600 hover:bg-amber-500 text-white h-8 text-xs" data-testid="cache-cleanup-delete-no-btn">
              {cleaningUp ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Limpiar "NO"
            </Button>
            <Button size="sm" onClick={handleCleanupDeleteErrors} disabled={cleaningUp} className="bg-rose-600 hover:bg-rose-500 text-white h-8 text-xs" data-testid="cache-cleanup-delete-err-btn">
              Limpiar "ERROR"
            </Button>
          </div>
        </div>
        {cleanupStats && (
          <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg text-xs text-zinc-300" data-testid="cache-cleanup-stats">
            {cleanupStats.mode === "count" ? (
              <div className="flex gap-4">
                <span>Total: <strong className="text-white">{cleanupStats.total}</strong></span>
                <span>SI: <strong className="text-green-400">{cleanupStats.si}</strong></span>
                <span>NO: <strong className="text-amber-400">{cleanupStats.no}</strong></span>
                <span>ERROR: <strong className="text-rose-400">{cleanupStats.error}</strong></span>
              </div>
            ) : (
              <span className="text-green-400">{cleanupStats.message}</span>
            )}
          </div>
        )}
      </div>
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input data-testid="cache-search-input" value={cacheSearch} onChange={e => setCacheSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo)} placeholder="Buscar numero..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder:text-zinc-600" />
          </div>
          <select data-testid="cache-filter-status" value={cacheFilter} onChange={e => { setCacheFilter(e.target.value); loadCache(cacheSearch, e.target.value, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo); }} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
            <option value="all">Todos</option><option value="si">Tiene WA</option><option value="no">No tiene</option><option value="error">Error</option>
          </select>
          {cache.verified_by_values && cache.verified_by_values.length > 0 && (
            <select data-testid="cache-filter-verified-by" value={cacheVerifiedBy} onChange={e => { setCacheVerifiedBy(e.target.value); loadCache(cacheSearch, cacheFilter, e.target.value, 1, cacheDateFrom, cacheDateTo); }} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Proveedor: Todos</option>
              {cache.verified_by_values.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          <Button data-testid="cache-search-btn" size="sm" onClick={() => loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, cacheDateTo)} className="bg-rose-600 hover:bg-rose-700"><Search className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Desde:</span>
            <input data-testid="cache-date-from" type="date" value={cacheDateFrom} onChange={e => { setCacheDateFrom(e.target.value); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, e.target.value, cacheDateTo); }} className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Hasta:</span>
            <input data-testid="cache-date-to" type="date" value={cacheDateTo} onChange={e => { setCacheDateTo(e.target.value); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, cacheDateFrom, e.target.value); }} className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" />
          </div>
          {(cacheDateFrom || cacheDateTo) && (
            <Button size="sm" variant="ghost" onClick={() => { setCacheDateFrom(""); setCacheDateTo(""); loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, 1, "", ""); }} className="text-zinc-500 hover:text-white text-xs h-7">Limpiar fechas</Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <select data-testid="reverify-provider" value={reverifyProvider} onChange={e => setReverifyProvider(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs">
              <option value="rapidapi_v2">API C</option>
            </select>
            <Button data-testid="reverify-btn" size="sm" onClick={handleReverify} disabled={reverifying || cache.total === 0} className="bg-amber-600 hover:bg-amber-700 text-white text-xs">
              {reverifying ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Re-verificando...</> : <><RefreshCw className="w-3 h-3 mr-1" />Re-verificar ({cache.total > 5000 ? "5000 max" : cache.total})</>}
            </Button>
          </div>
        </div>
        {cache.results.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-lg border border-zinc-800">
              <table className="w-full text-left">
                <thead className="bg-zinc-800/50"><tr>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Numero</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase text-center">WA</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Verificado</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Proveedor</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Fuente</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase text-center">Acciones</th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-800">
                  {cache.results.map((r, i) => (
                    <tr key={i} className="hover:bg-zinc-800/30">
                      <td className="px-4 py-2.5 font-mono text-white text-sm">{r.numero}</td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          data-testid={`cache-toggle-${r.numero}`}
                          onClick={() => handleToggleStatus(r.numero, r.tiene_whatsapp)}
                          disabled={editingPhone === r.numero}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-all hover:scale-105 ${
                            r.tiene_whatsapp === "SI" 
                              ? "bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30" 
                              : "bg-zinc-700/50 text-zinc-400 border border-zinc-600 hover:bg-zinc-700"
                          }`}
                        >
                          {editingPhone === r.numero ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                          {r.tiene_whatsapp}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 text-xs">{r.verified_at ? formatDate(r.verified_at) : "--"}</td>
                      <td className="px-4 py-2.5">
                        {r.verified_by ? <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">{r.verified_by}</Badge> : <span className="text-zinc-600 text-xs">--</span>}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-500 text-xs">{r.source || "--"}</td>
                      <td className="px-4 py-2.5 text-center">
                        <Button
                          data-testid={`cache-delete-${r.numero}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(r.numero)}
                          className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-zinc-500">
                Mostrando {((cachePage - 1) * 50) + 1}-{Math.min(cachePage * 50, cache.total)} de {cache.total} | 
                <span className="text-green-400 ml-1">{cache.si_total} SI</span> · <span className="text-zinc-400">{cache.no_total} NO</span>
              </span>
              <div className="flex items-center gap-2">
                <Button data-testid="cache-page-prev" size="sm" variant="outline" disabled={cachePage <= 1} onClick={() => loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage - 1, cacheDateFrom, cacheDateTo)} className="bg-zinc-900 border-zinc-700 text-zinc-300 text-xs">Ant</Button>
                <span className="text-xs text-zinc-500">Pag {cachePage} / {totalPages}</span>
                <Button data-testid="cache-page-next" size="sm" variant="outline" disabled={cachePage >= totalPages} onClick={() => loadCache(cacheSearch, cacheFilter, cacheVerifiedBy, cachePage + 1, cacheDateFrom, cacheDateTo)} className="bg-zinc-900 border-zinc-700 text-zinc-300 text-xs">Sig</Button>
              </div>
            </div>
          </>
        ) : <p className="text-zinc-500 text-sm text-center py-4">Sin resultados</p>}
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar del cache</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Estas seguro de eliminar <span className="font-mono text-white">{deleteConfirm}</span> del cache de WhatsApp? Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cache-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== USERS TAB (email+password mgmt) ====================
const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newCompanyId, setNewCompanyId] = useState("");
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetForUserId, setResetForUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [deleteForUserId, setDeleteForUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    const [uRes, cRes] = await Promise.all([
      apiCall("GET", `${API}/admin/users`),
      apiCall("GET", `${API}/admin/companies`),
    ]);
    if (uRes.ok) setUsers(uRes.data || []);
    if (cRes.ok) {
      // /api/admin/companies returns {companies: [...], total, ...} - unwrap for older shape too
      const raw = cRes.data;
      setCompanies(Array.isArray(raw) ? raw : (raw?.companies || []));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newPassword || !newCompanyId) {
      toast.error("Email, contraseña y empresa son obligatorios");
      return;
    }
    setSubmitting(true);
    const { ok, data } = await apiCall("POST", `${API}/admin/users`, {
      email: newEmail.trim().toLowerCase(),
      password: newPassword,
      company_id: newCompanyId,
      name: newName.trim(),
    });
    setSubmitting(false);
    if (ok) {
      toast.success(`Usuario ${data.email} creado`);
      setNewEmail(""); setNewPassword(""); setNewCompanyId(""); setNewName("");
      setShowCreate(false);
      load();
    } else {
      toast.error(data?.detail || "Error al crear usuario");
    }
  };

  const handleReset = async () => {
    if (!resetForUserId || !resetPassword) return;
    const { ok, data } = await apiCall("POST", `${API}/admin/users/${resetForUserId}/reset-password`, {
      new_password: resetPassword,
    });
    if (ok) {
      toast.success("Contraseña reseteada — el usuario debe volver a loguearse");
      setResetForUserId(null); setResetPassword("");
    } else {
      toast.error(data?.detail || "Error al resetear contraseña");
    }
  };

  const handleDelete = async () => {
    if (!deleteForUserId) return;
    const { ok, data } = await apiCall("DELETE", `${API}/admin/users/${deleteForUserId}`);
    if (ok) {
      toast.success("Usuario eliminado");
      setDeleteForUserId(null);
      load();
    } else {
      toast.error(data?.detail || "Error al eliminar");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-400" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-users-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-rose-400" />Usuarios ({users.length})
        </h3>
        <Button onClick={() => setShowCreate(v => !v)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-users-create-btn">
          <Plus className="w-4 h-4 mr-2" />{showCreate ? "Cancelar" : "Crear Usuario"}
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 space-y-3" data-testid="admin-users-create-form">
          <h4 className="text-sm font-bold text-white">Crear usuario con email + contraseña</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Email</label>
              <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="usuario@empresa.com" className="bg-zinc-950 border-zinc-800 text-white" required data-testid="admin-users-email-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Nombre (opcional)</label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Juan Pérez" className="bg-zinc-950 border-zinc-800 text-white" data-testid="admin-users-name-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Contraseña (≥8 chars + 1 número)</label>
              <Input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="MiPass1234" className="bg-zinc-950 border-zinc-800 text-white font-mono" required data-testid="admin-users-password-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Empresa</label>
              <select value={newCompanyId} onChange={e => setNewCompanyId(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-md h-10 px-3 text-sm" required data-testid="admin-users-company-select">
                <option value="">Elegí una empresa...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name}{c.is_active ? "" : " (inactiva)"}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="text-zinc-400">Cancelar</Button>
            <Button type="submit" disabled={submitting} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-users-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Crear
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {users.map(u => (
          <div key={u.user_id} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`admin-user-row-${u.user_id}`}>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-white">{u.name || u.email.split("@")[0]}</span>
                <Badge className={u.role === "superadmin" ? "bg-rose-500/10 text-rose-300 border-rose-500/20" : u.role === "company" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>{u.role}</Badge>
                {u.has_password ? (
                  <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/20">🔑 Password</Badge>
                ) : (
                  <Badge className="bg-zinc-700 text-zinc-400 border-white/10">Google only</Badge>
                )}
              </div>
              <p className="text-xs text-zinc-400">{u.email}</p>
              {u.company_name && <p className="text-xs text-zinc-500 mt-0.5"><Building2 className="w-3 h-3 inline mr-1" />{u.company_name}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setResetForUserId(u.user_id); setResetPassword(""); }} className="text-amber-400 hover:text-amber-300" data-testid={`admin-user-reset-btn-${u.user_id}`}>
                <Key className="w-3.5 h-3.5 mr-1" />Resetear contraseña
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleteForUserId(u.user_id)} className="text-rose-400 hover:text-rose-300" data-testid={`admin-user-delete-btn-${u.user_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!resetForUserId} onOpenChange={o => !o && setResetForUserId(null)}>
        <DialogContent className="sm:max-w-md bg-zinc-950 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Key className="w-5 h-5 text-amber-400" />Resetear contraseña</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400">El usuario será desloggeado de todas sus sesiones y deberá usar la contraseña nueva.</p>
          <Input type="text" value={resetPassword} onChange={e => setResetPassword(e.target.value)} placeholder="Nueva contraseña (≥8 + 1 número)" className="bg-zinc-900 border-zinc-800 text-white font-mono" data-testid="admin-users-reset-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setResetForUserId(null)} className="text-zinc-400">Cancelar</Button>
            <Button onClick={handleReset} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-users-reset-confirm-btn">Aplicar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteForUserId} onOpenChange={o => !o && setDeleteForUserId(null)}>
        <AlertDialogContent className="bg-zinc-950 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Se borra el usuario y todas sus sesiones activas. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-users-delete-confirm-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


// ==================== STATS TAB ====================
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try { const { ok, data } = await apiCall("GET", `${API}/admin/stats`); if (ok) setStats(data); }
      catch {} finally { setLoading(false); }
    };
    load();
    // Load RapidAPI 6-month history in parallel
    (async () => {
      try {
        const { ok, data } = await apiCall("GET", `${API}/admin/rapidapi-usage/history`);
        if (ok) setUsage(data);
      } catch {}
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-400" /></div>;
  if (!stats) return <p className="text-zinc-500 text-center">Error cargando estadisticas</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="p-5 rounded-lg bg-zinc-900/50 border border-white/10 text-center"><p className="text-3xl font-bold text-white">{stats.total_companies}</p><p className="text-xs text-zinc-500 mt-1">Empresas</p></div>
        <div className="p-5 rounded-lg bg-zinc-900/50 border border-white/10 text-center"><p className="text-3xl font-bold text-emerald-400">{stats.active_companies}</p><p className="text-xs text-zinc-500 mt-1">Activas</p></div>
        <div className="p-5 rounded-lg bg-zinc-900/50 border border-white/10 text-center"><p className="text-3xl font-bold text-violet-400">{stats.total_campaigns}</p><p className="text-xs text-zinc-500 mt-1">Campanas</p></div>
        <div className="p-5 rounded-lg bg-zinc-900/50 border border-white/10 text-center"><p className="text-3xl font-bold text-blue-400">{stats.total_users}</p><p className="text-xs text-zinc-500 mt-1">Usuarios</p></div>
        <div className="p-5 rounded-lg bg-zinc-900/50 border border-white/10 text-center"><p className="text-3xl font-bold text-green-400">{stats.total_whatsapp_cache}</p><p className="text-xs text-zinc-500 mt-1">Cache WPP</p></div>
      </div>

      <RapidApiHistoryChart data={usage} />

      <h3 className="text-lg font-bold text-white">Detalle por Empresa</h3>
      <div className="space-y-3">
        {stats.companies.map(comp => (
          <div key={comp.company_id} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-rose-400" />
                <span className="font-medium text-white">{comp.name}</span>
                <Badge className={comp.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>{comp.is_active ? "Activa" : "Inactiva"}</Badge>
              </div>
              <div className="flex gap-2 mt-1">{(comp.emails || []).map((e, i) => <span key={i} className="text-xs text-zinc-500">{e}</span>)}</div>
            </div>
            <div className="text-right"><p className="text-2xl font-bold text-violet-400">{comp.campaigns}</p><p className="text-xs text-zinc-500">campanas</p></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== RAPIDAPI 6-MONTH HISTORY CHART ====================
const RapidApiHistoryChart = ({ data }) => {
  if (!data?.months?.length) return null;
  const providerKeys = Object.keys(data.providers || {});
  if (!providerKeys.length) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6" data-testid="rapidapi-history-chart">
        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-violet-400" />Historial de Uso RapidAPI (últimos 6 meses)
        </h3>
        <p className="text-sm text-zinc-500">Sin datos aún — se registrarán cuando corran las próximas campañas de WhatsApp.</p>
      </div>
    );
  }

  // Format month labels: "2026-09" -> "Sep 26"
  const monthLabels = {
    "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun",
    "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic"
  };
  const formatMonth = (m) => {
    const [y, mo] = m.split("-");
    return `${monthLabels[mo] || mo} ${y.slice(2)}`;
  };

  // Build chart data: [{month: "Sep 26", C: 82340, B: 1250}, ...]
  const chartData = data.months.map((m, i) => {
    const row = { month: formatMonth(m) };
    providerKeys.forEach(pid => {
      row[pid] = data.providers[pid][i] || 0;
    });
    return row;
  });

  const providerColors = { C: "#a78bfa", B: "#34d399", A: "#60a5fa" };
  const fmtK = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n/1_000).toFixed(0)}K` : n;

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6" data-testid="rapidapi-history-chart">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-400" />Historial de Uso RapidAPI
          </h3>
          <p className="text-xs text-zinc-500 mt-1">Últimos 6 meses · requests consumidos por proveedor</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          {providerKeys.map(pid => (
            <div key={pid} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: providerColors[pid] || "#71717a" }} />
              <span className="text-zinc-300">API {pid}</span>
              {data.quotas?.[pid] > 0 && <span className="text-zinc-500">(cuota {fmtK(data.quotas[pid])})</span>}
            </div>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <RBarChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
          <XAxis dataKey="month" stroke="#71717a" tick={{ fontSize: 12 }} />
          <YAxis stroke="#71717a" tick={{ fontSize: 12 }} tickFormatter={fmtK} />
          <Tooltip
            contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: "#e4e4e7" }}
            itemStyle={{ color: "#e4e4e7" }}
            formatter={(v) => v.toLocaleString("es-AR")}
          />
          {providerKeys.map(pid => (
            <Bar key={pid} dataKey={pid} fill={providerColors[pid] || "#71717a"} radius={[4, 4, 0, 0]} maxBarSize={48} />
          ))}
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== ENACOM DATA TAB ====================
const AdminEnacomTab = () => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [totalCodes, setTotalCodes] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchEnacom = async (q = "") => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/enacom?search=${encodeURIComponent(q)}&limit=50`);
      if (ok) { setResults(data.results || []); setTotalCodes(data.total_area_codes || 0); }
    } catch {}
    finally { setLoading(false); }
  };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEnacom(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-enacom-section">
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-400" />Base de Datos ENACOM
        </h2>
        <p className="text-sm text-zinc-400 mb-4">Base de numeracion telefonica argentina. Total: {totalCodes} codigos de area.</p>
        <div className="flex gap-3 mb-4">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por codigo de area, localidad u operador..." className="bg-zinc-950 border-zinc-800 text-white flex-1" data-testid="enacom-search" onKeyDown={e => e.key === "Enter" && fetchEnacom(search)} />
          <Button onClick={() => fetchEnacom(search)} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="enacom-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {results.map(r => (
            <div key={r.area_code} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20 font-mono">{r.area_code}</Badge>
                <span className="text-xs text-zinc-500">{r.total_blocks} bloques</span>
              </div>
              {r.sample_blocks?.map((b, i) => (
                <div key={i} className="flex items-center gap-4 text-xs text-zinc-400 py-1 border-t border-zinc-800">
                  <span className="font-mono text-zinc-500 w-16">{b.bloque}</span>
                  <span className="text-zinc-300">{b.localidad}</span>
                  <span>{b.operador}</span>
                  <Badge variant="outline" className="text-[10px] border-zinc-700">{b.modalidad}</Badge>
                </div>
              ))}
            </div>
          ))}
          {results.length === 0 && !loading && <p className="text-zinc-600 text-center py-6">Sin resultados</p>}
        </div>
      </div>
    </div>
  );
};

// ==================== GLOBALWPP CAMPAIGNS TAB ====================
const AdminGlobalWPPTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [resumingId, setResumingId] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const [stoppingAll, setStoppingAll] = useState(false);
  const [apiTest, setApiTest] = useState(null);
  const [testingApi, setTestingApi] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);

  const fetchCampaigns = async (p = 1) => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/globalwpp-campaigns?page=${p}&limit=30`);
      if (ok) { setCampaigns(data.campaigns || []); setTotal(data.total || 0); setPage(p); }
    } catch {}
    finally { setLoading(false); }
  };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCampaigns(); }, []);

  const getApiLabel = (c) => {
    const p = c.provider || c.source || "";
    if (p === "rapidapi_both") return "C";
    if (p === "rapidapi_v2") return "C";
    if (p === "rapidapi") return "B";
    if (p === "globalwpp") return "A";
    return p || "--";
  };

  const getApiBadgeClass = (c) => {
    const p = c.provider || c.source || "";
    if (p === "rapidapi_both") return "bg-purple-500/15 text-purple-400 border-purple-500/20";
    if (p === "rapidapi_v2") return "bg-cyan-500/15 text-cyan-400 border-cyan-500/20";
    if (p === "rapidapi") return "bg-blue-500/15 text-blue-400 border-blue-500/20";
    return "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
  };

  const getProgress = (c) => {
    const total = c.total_numbers || 0;
    const si = c.valido_count || 0;
    const no = c.invalido_count || 0;
    const err = c.error_count || 0;
    const skipped = c.skipped_count || 0;
    let done = si + no + err + skipped;
    // For completed campaigns, trust the status — fill the gap as skipped
    if (c.status === "completed" && total > 0 && done < total) {
      done = total;
    }
    const pct = total > 0 ? Math.min(Math.round((done / total) * 100), 100) : 0;
    const remaining = Math.max(total - done, 0);
    
    // Estimate time remaining
    let eta = "";
    if (remaining > 0 && c.created_at && (c.status === "processing" || c.status === "sent")) {
      const created = new Date(c.created_at);
      const elapsed = (Date.now() - created.getTime()) / 1000;
      if (done > 0 && elapsed > 10) {
        const rate = done / elapsed;
        const secsLeft = remaining / rate;
        if (secsLeft < 60) eta = `~${Math.round(secsLeft)}s`;
        else if (secsLeft < 3600) eta = `~${Math.round(secsLeft / 60)}min`;
        else eta = `~${Math.round(secsLeft / 3600)}h`;
      }
    }
    return { pct, done, remaining, si, no, err, eta };
  };

  const handleTogglePause = async (campaignId) => {
    setTogglingId(campaignId);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/wa-campaigns/${campaignId}/toggle-pause`);
      if (ok) {
        toast.success(data.message);
        fetchCampaigns(page);
      } else {
        toast.error(data?.detail || "Error");
      }
    } catch { toast.error("Error al cambiar estado"); }
    finally { setTogglingId(null); }
  };

  const handleResume = async (analysisId, campaignId) => {
    // Prefer analysis_id, fallback to campaign_id
    const resumeId = analysisId || (campaignId ? `cid:${campaignId}` : null);
    if (!resumeId) {
      toast.error("No se puede reintentar: sin ID de campaña");
      return;
    }
    setResumingId(resumeId);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/wa-resume/${resumeId}`);
      if (ok) {
        toast.success(data.message || "Campaña reiniciada");
        fetchCampaigns(page);
      } else {
        toast.error(data?.detail || "Error al reiniciar campaña");
      }
    } catch { toast.error("Error de conexión al reiniciar"); }
    finally { setResumingId(null); }
  };

  const handleTestApi = async () => {
    setTestingApi(true);
    setApiTest(null);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/test-wa-api`);
      if (ok) {
        setApiTest(data);
      } else {
        toast.error(data?.detail || "Error al testear");
        setApiTest({ error: data?.detail || "Error" });
      }
    } catch { toast.error("Error de conexión"); }
    finally { setTestingApi(false); }
  };

  const fetchProcessingStatus = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/wa-processing-status`);
      if (ok) setProcessingStatus(data);
    } catch {}
  };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProcessingStatus(); }, []);

  const handleStopCampaign = async (campaignId) => {
    if (!window.confirm("¿Detener permanentemente esta campaña? No se puede reanudar.")) return;
    setStoppingId(campaignId);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/wa-campaigns/${campaignId}/stop`);
      if (ok) {
        toast.success(data.message || "Campaña detenida");
        fetchCampaigns(page);
        fetchProcessingStatus();
      } else {
        toast.error(data?.detail || "Error");
      }
    } catch { toast.error("Error al detener"); }
    finally { setStoppingId(null); }
  };

  const handleStopAll = async () => {
    if (!window.confirm("⚠️ EMERGENCIA: ¿Detener TODAS las campañas activas y deshabilitar auto-trigger? Esto detendrá inmediatamente todas las peticiones a las APIs.")) return;
    setStoppingAll(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/stop-all-wa-campaigns`);
      if (ok) {
        toast.success(data.message || "Todas las campañas detenidas");
        fetchCampaigns(page);
        fetchProcessingStatus();
      } else {
        toast.error(data?.detail || "Error");
      }
    } catch { toast.error("Error al detener todo"); }
    finally { setStoppingAll(false); }
  };

  const handleEnableProcessing = async () => {
    if (!window.confirm("¿Reactivar el auto-trigger de WhatsApp?")) return;
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/enable-wa-processing`);
      if (ok) {
        toast.success(data.message || "Auto-trigger reactivado");
        fetchProcessingStatus();
      } else {
        toast.error(data?.detail || "Error");
      }
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-globalwpp-section">
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-400" />Campañas WhatsApp
            </h2>
            <p className="text-sm text-zinc-400">Todas las verificaciones de WhatsApp del sistema. Total: {total}</p>
          </div>
          <div className="flex items-center gap-2">
            {processingStatus && (
              <div className="flex items-center gap-2 text-xs mr-2">
                <span className={`px-2 py-1 rounded ${processingStatus.auto_trigger_enabled ? "bg-green-500/15 text-green-400" : "bg-rose-500/15 text-rose-400"}`}>
                  Auto-trigger: {processingStatus.auto_trigger_enabled ? "ON" : "OFF"}
                </span>
                {processingStatus.active_campaigns > 0 && (
                  <span className="px-2 py-1 rounded bg-blue-500/15 text-blue-400">{processingStatus.active_campaigns} activas</span>
                )}
                {!processingStatus.auto_trigger_enabled && (
                  <Button onClick={handleEnableProcessing} size="sm" className="bg-green-600 hover:bg-green-500 text-white h-7 text-xs" data-testid="enable-wa-processing-btn">
                    Reactivar
                  </Button>
                )}
              </div>
            )}
            <Button onClick={handleStopAll} disabled={stoppingAll} size="sm" className="bg-rose-700 hover:bg-rose-600 text-white" data-testid="stop-all-wa-btn">
              {stoppingAll ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Square className="w-3 h-3 mr-1" />}
              DETENER TODO
            </Button>
            <Button onClick={() => fetchCampaigns(1)} disabled={loading} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" data-testid="refresh-globalwpp">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {/* Test API Panel */}
        <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
          <div className="flex items-center gap-3">
            <Button onClick={handleTestApi} disabled={testingApi} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="test-wa-api-btn">
              {testingApi ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Testeando...</> : <><Activity className="w-3 h-3 mr-1" /> Test APIs</>}
            </Button>
            {apiTest && (
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-400 font-medium">API C:</span>
                  {apiTest.api_c?.status === "OK" ? (
                    <span className="text-green-400">{apiTest.api_c.time_ms}ms</span>
                  ) : (
                    <span className="text-rose-400">{apiTest.api_c?.error || "Error"}</span>
                  )}
                </div>
                {!apiTest.rapidapi_key_set && <span className="text-rose-400 font-bold">KEY NO CONFIGURADA</span>}
              </div>
            )}
          </div>
        </div>
        {campaigns.length > 0 ? (
          <div className="rounded-lg border border-zinc-800">
            <table className="w-full text-left table-fixed">
              <thead className="bg-zinc-800/50">
                <tr>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[22%]">Campaña</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[13%]">Empresa</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[30%]">Progreso</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[13%]">Estado</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[10%]">API</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase w-[12%] text-right">Accion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {campaigns.map((c, i) => {
                  const isProcessing = c.status === "processing" || c.status === "sent" || c.status === "stuck";
                  const isPaused = c.paused === true;
                  const isStuck = c.status === "stuck";
                  const p = getProgress(c);
                  const isIncomplete = c.status === "completed" && p.pct < 100 && (c.total_numbers || 0) > 0;
                  return (
                    <tr key={c.campaign_id || i} className="hover:bg-zinc-800/30">
                      <td className="px-3 py-2 w-[22%]">
                        <p className="text-white text-sm font-medium truncate" title={c.campaign_name}>{c.campaign_name || "Sin nombre"}</p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-zinc-500 text-[10px]">{formatDate(c.created_at)}</p>
                          <span className="text-zinc-600 text-[10px] font-mono cursor-pointer hover:text-zinc-400" title={`ID: ${c.campaign_id}`} onClick={() => { navigator.clipboard.writeText(c.campaign_id); toast.success("ID copiado"); }} data-testid={`campaign-id-${i}`}>
                            #{(c.campaign_id || "").slice(0, 8)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 w-[13%]">
                        <span className="text-violet-400 text-[10px] font-medium">{c.company_name || "--"}</span>
                      </td>
                      <td className="px-3 py-2 w-[30%]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="flex justify-between text-[10px] mb-0.5">
                              <span className="text-zinc-400">{p.done.toLocaleString()}/{(c.total_numbers||0).toLocaleString()}</span>
                              <span className="text-white font-bold">{p.pct}%</span>
                            </div>
                            <div className="w-full bg-zinc-700/50 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full transition-all ${p.pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{width: `${Math.min(p.pct, 100)}%`}} />
                            </div>
                            <div className="flex justify-between text-[10px] mt-0.5">
                              <span className="text-green-400">{p.si} SI</span>
                              <span className="text-zinc-500">{p.no} NO</span>
                              {p.err > 0 && <span className="text-rose-400">{p.err} ERR</span>}
                              {p.eta && <span className="text-amber-400">{p.eta}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 w-[13%]">
                        <Badge className={`text-[10px] ${
                          isPaused ? "bg-amber-500/15 text-amber-400" :
                          c.status === "stopped" ? "bg-rose-700/20 text-rose-400" :
                          isIncomplete ? "bg-orange-500/15 text-orange-400" :
                          c.status === "completed" ? "bg-emerald-500/15 text-emerald-400" :
                          c.status === "processing" ? "bg-blue-500/15 text-blue-400" :
                          c.status === "sent" ? "bg-amber-500/15 text-amber-400" :
                          c.status === "interrupted" ? "bg-orange-500/15 text-orange-400" :
                          c.status === "stuck" ? "bg-rose-500/15 text-rose-400" :
                          "bg-zinc-500/15 text-zinc-400"
                        }`}>
                          {isPaused ? "Pausado" : c.status === "stopped" ? "Detenido" : isIncomplete ? "Incompleto" : c.status === "completed" ? "Completo" : c.status === "processing" ? "Procesando" : c.status === "sent" ? "En proceso" : c.status === "interrupted" ? "Interrumpido" : c.status === "stuck" ? "Trabada" : c.status || "--"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 w-[10%]">
                        <Badge className={getApiBadgeClass(c) + " text-[10px]"}>
                          {getApiLabel(c)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 w-[12%] text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isProcessing && !isStuck && c.status !== "stopped" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleTogglePause(c.campaign_id)}
                              disabled={togglingId === c.campaign_id}
                              className={`h-7 w-7 p-0 ${isPaused ? "text-green-400 hover:text-green-300 hover:bg-green-500/10" : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"}`}
                              data-testid={`toggle-pause-${c.campaign_id}`}
                              title={isPaused ? "Reanudar" : "Pausar"}
                            >
                              {togglingId === c.campaign_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          {isProcessing && c.status !== "stopped" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStopCampaign(c.campaign_id)}
                              disabled={stoppingId === c.campaign_id}
                              className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                              title="Detener permanentemente"
                              data-testid={`stop-campaign-${c.campaign_id}`}
                            >
                              {stoppingId === c.campaign_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          {(isProcessing || c.status === "interrupted" || isIncomplete) && (c.analysis_id || c.campaign_id) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResume(c.analysis_id, c.campaign_id)}
                              disabled={resumingId === (c.analysis_id || `cid:${c.campaign_id}`)}
                              className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                              title={isIncomplete ? "Reintentar verificacion WA (incompleta)" : "Reiniciar verificacion WA"}
                              data-testid={`resume-${c.campaign_id}`}
                            >
                              {resumingId === (c.analysis_id || `cid:${c.campaign_id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          {c.status === "completed" && c.campaign_id && (
                            <div className="flex items-center gap-0.5">
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-green-400 hover:bg-green-500/10" title="Exportar Con WA" data-testid={`export-si-${c.campaign_id}`}
                                onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/campaigns/${c.campaign_id}/export?filter_by=si&format=xlsx`; document.body.appendChild(iframe); setTimeout(() => iframe.remove(), 10000); }}>SI</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-zinc-400 hover:bg-zinc-500/10" title="Exportar Sin WA" data-testid={`export-no-${c.campaign_id}`}
                                onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/campaigns/${c.campaign_id}/export?filter_by=no&format=xlsx`; document.body.appendChild(iframe); setTimeout(() => iframe.remove(), 10000); }}>NO</Button>
                              {(c.error_count || 0) > 0 && (
                                <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-rose-400 hover:bg-rose-500/10" title="Exportar Errores" data-testid={`export-err-${c.campaign_id}`}
                                  onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/campaigns/${c.campaign_id}/export?filter_by=error&format=xlsx`; document.body.appendChild(iframe); setTimeout(() => iframe.remove(), 10000); }}>ERR</Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-white hover:bg-zinc-500/10" title="Exportar Todos" data-testid={`export-all-${c.campaign_id}`}
                                onClick={() => { const iframe = document.createElement("iframe"); iframe.style.display = "none"; iframe.src = `${API}/whatsapp/campaigns/${c.campaign_id}/export?filter_by=all&format=xlsx`; document.body.appendChild(iframe); setTimeout(() => iframe.remove(), 10000); }}>ALL</Button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && <p className="text-zinc-600 text-center py-6">No hay campañas WhatsApp</p>
        )}
        {total > 30 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchCampaigns(page - 1)} className="border-zinc-700 text-zinc-400">Anterior</Button>
            <span className="text-zinc-500 text-xs self-center">Pag {page} de {Math.ceil(total / 30)}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => fetchCampaigns(page + 1)} className="border-zinc-700 text-zinc-400">Siguiente</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;

// ==================== NIC.AR RDAP TAB ====================
const AdminNicArTab = () => {
  const [searchDomain, setSearchDomain] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [cachedDomains, setCachedDomains] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cacheSearch, setCacheSearch] = useState("");
  const [cacheLoading, setCacheLoading] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCachedDomains(); }, []);

  const fetchCachedDomains = async (q = "", p = 1) => {
    setCacheLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/rdap-domains?search=${encodeURIComponent(q)}&page=${p}&limit=30`);
      if (ok) { setCachedDomains(data.domains || []); setTotal(data.total || 0); setPage(p); }
    } catch {}
    finally { setCacheLoading(false); }
  };

  const handleLookup = async () => {
    const d = searchDomain.trim().toLowerCase();
    if (!d) return toast.error("Ingresa un dominio");
    if (!d.endsWith(".ar")) return toast.error("Solo dominios .ar estan soportados");
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/rdap/${encodeURIComponent(d)}`);
      if (!ok) throw new Error("Error en la consulta");
      setLookupResult(data);
      fetchCachedDomains(cacheSearch, page);
    } catch (e) { toast.error(e.message || "Error al consultar RDAP"); }
    finally { setLookupLoading(false); }
  };

  const handleDelete = async (domain) => {
    try {
      const { ok, data } = await apiCall("DELETE", `${API}/admin/rdap-domains/${encodeURIComponent(domain)}`);
      if (!ok) throw new Error(data.detail || "Error");
      toast.success(`Dominio ${domain} eliminado del cache`);
      setDomainToDelete(null);
      fetchCachedDomains(cacheSearch, page);
    } catch (e) { toast.error(e.message || "Error al eliminar"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-nicar-section">
      {/* RDAP Lookup */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Globe className="w-5 h-5 text-cyan-400" />Consulta RDAP - NIC.ar
        </h2>
        <p className="text-sm text-zinc-400 mb-4">Consulta datos de registro de dominios <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-cyan-400 text-xs">.ar</code> via RDAP.</p>
        <div className="flex gap-3">
          <Input
            value={searchDomain}
            onChange={e => setSearchDomain(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLookup()}
            placeholder="ejemplo.com.ar"
            className="bg-zinc-950 border-zinc-800 text-white flex-1 font-mono"
            data-testid="rdap-lookup-input"
          />
          <Button onClick={handleLookup} disabled={lookupLoading} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="rdap-lookup-btn">
            {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}Consultar
          </Button>
        </div>

        {lookupResult && (
          <div className="mt-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg" data-testid="rdap-lookup-result">
            {lookupResult.found ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-white font-bold">{lookupResult.domain}</span>
                  <Badge className={lookupResult.status === "active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}>
                    {lookupResult.status === "active" ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {lookupResult.registrant && (
                    <div>
                      <span className="text-xs text-zinc-500 block mb-0.5">Registrante</span>
                      <span className="text-zinc-200">{lookupResult.registrant}</span>
                    </div>
                  )}
                  {lookupResult.registration_date && (
                    <div>
                      <span className="text-xs text-zinc-500 block mb-0.5">Registro</span>
                      <span className="text-zinc-300">{lookupResult.registration_date}</span>
                    </div>
                  )}
                  {lookupResult.expiration_date && (
                    <div>
                      <span className="text-xs text-zinc-500 block mb-0.5">Vencimiento</span>
                      <span className="text-zinc-300">{lookupResult.expiration_date}</span>
                    </div>
                  )}
                  {lookupResult.nameservers?.length > 0 && (
                    <div className="col-span-2 md:col-span-4">
                      <span className="text-xs text-zinc-500 block mb-0.5">Nameservers</span>
                      <div className="flex flex-wrap gap-1.5">
                        {lookupResult.nameservers.map((ns, i) => (
                          <Badge key={i} className="bg-zinc-700 text-zinc-300 border-zinc-600 text-xs font-mono">{ns}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{lookupResult.error || "Dominio no encontrado"}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cached Domains */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-400" />Dominios en Cache ({total})
            </h2>
            <p className="text-sm text-zinc-400">Dominios consultados previamente almacenados en la base de datos.</p>
          </div>
          <Button onClick={() => fetchCachedDomains(cacheSearch, 1)} disabled={cacheLoading} variant="outline" className="bg-zinc-900 border-zinc-700 text-zinc-300" data-testid="refresh-rdap-cache">
            {cacheLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={cacheSearch}
              onChange={e => setCacheSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && fetchCachedDomains(cacheSearch, 1)}
              placeholder="Buscar dominio..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder:text-zinc-600"
              data-testid="rdap-cache-search"
            />
          </div>
          <Button size="sm" onClick={() => fetchCachedDomains(cacheSearch, 1)} className="bg-cyan-600 hover:bg-cyan-700">
            <Search className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {cachedDomains.map((entry) => (
            <div key={entry.domain} className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg" data-testid={`cached-domain-${entry.domain}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-white font-medium">{entry.domain}</span>
                  {entry.data?.status && (
                    <Badge className={entry.data.status === "active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs" : "bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs"}>
                      {entry.data.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  )}
                  {entry.data?.registrant && <span className="text-xs text-zinc-400">{entry.data.registrant}</span>}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                  {entry.data?.registration_date && <span>Reg: {entry.data.registration_date}</span>}
                  {entry.data?.expiration_date && <span>Exp: {entry.data.expiration_date}</span>}
                  <span>Cache: {formatDate(entry.timestamp)}</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDomainToDelete(entry.domain)} className="text-zinc-500 hover:text-rose-400" data-testid={`delete-domain-${entry.domain}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {cachedDomains.length === 0 && !cacheLoading && (
            <p className="text-zinc-600 text-center py-6">No hay dominios en cache. Usa la consulta RDAP para agregar.</p>
          )}
        </div>

        {total > 30 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchCachedDomains(cacheSearch, page - 1)} className="border-zinc-700 text-zinc-400">Anterior</Button>
            <span className="text-zinc-500 text-xs self-center">Pag {page} de {Math.ceil(total / 30)}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => fetchCachedDomains(cacheSearch, page + 1)} className="border-zinc-700 text-zinc-400">Siguiente</Button>
          </div>
        )}
      </div>

      <AlertDialog open={!!domainToDelete} onOpenChange={() => setDomainToDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar dominio del cache?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Se eliminara "{domainToDelete}" del cache RDAP.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(domainToDelete)} className="bg-rose-600 hover:bg-rose-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


const PlatformSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    rapidapi_key: "",
    rapidapi_host: "bulk-whatsapp-validator.p.rapidapi.com",
    notification_phones: [],
    ultramsg_instance: "",
    ultramsg_token: "",
    mp_access_token: "",
    mp_public_key: "",
    wa_auto_trigger_enabled: true,
  });
  const [newPhone, setNewPhone] = useState("");
  const [plans, setPlans] = useState([]);
  const [editPlan, setEditPlan] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchPlans();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/settings/platform`);
      if (ok) setSettings(data);
    } catch {}
    setLoading(false);
  };

  const fetchPlans = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/plans`);
      if (ok) setPlans(data.plans || []);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/settings/platform`, settings);
      if (ok) toast.success("Configuracion guardada");
      else toast.error(data?.detail || "Error");
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const addPhone = () => {
    const phone = newPhone.trim();
    if (!phone) return;
    if (settings.notification_phones.includes(phone)) return toast.error("Ya existe");
    setSettings(s => ({ ...s, notification_phones: [...s.notification_phones, phone] }));
    setNewPhone("");
  };

  const removePhone = (phone) => {
    setSettings(s => ({ ...s, notification_phones: s.notification_phones.filter(p => p !== phone) }));
  };

  const handleSavePlan = async (plan) => {
    try {
      const { ok, data } = await apiCall("POST", `${API}/admin/plans`, plan);
      if (ok) { toast.success(data.message); setEditPlan(null); fetchPlans(); }
      else toast.error(data?.detail || "Error");
    } catch { toast.error("Error"); }
  };

  const handleDeletePlan = async (planId) => {
    try {
      const { ok } = await apiCall("DELETE", `${API}/admin/plans/${planId}`);
      if (ok) { toast.success("Plan eliminado"); fetchPlans(); }
    } catch { toast.error("Error"); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>;

  return (
    <div className="space-y-6" data-testid="platform-settings">
      {/* MercadoPago */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <CreditCard className="w-4 h-4 text-blue-400" />MercadoPago
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Access Token</Label>
            <Input value={settings.mp_access_token} onChange={e => setSettings(s => ({ ...s, mp_access_token: e.target.value }))} placeholder="APP_USR-..." type="password" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="mp-access-token" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Public Key</Label>
            <Input value={settings.mp_public_key} onChange={e => setSettings(s => ({ ...s, mp_public_key: e.target.value }))} placeholder="APP_USR-..." type="password" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="mp-public-key" />
          </div>
        </div>
      </div>

      {/* Plans CRUD */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Layers className="w-4 h-4 text-violet-400" />Planes
          </h3>
          <Button size="sm" onClick={() => setEditPlan({ plan_id: "", name: "", price: 0, balance: 0, discount_pct: 0, phone_price: 0.80, wa_price: 2.00, active: true, order: plans.length })} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7" data-testid="add-plan-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Nuevo
          </Button>
        </div>
        <div className="space-y-2">
          {plans.map(p => (
            <div key={p.plan_id} className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-800 rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white text-sm">{p.name}</span>
                  {!p.active && <Badge className="bg-zinc-700 text-zinc-400 text-[10px]">Inactivo</Badge>}
                  {p.discount_pct > 0 && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">-{p.discount_pct}%</Badge>}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">
                  ${p.price.toLocaleString("es-AR")} {p.price > 0 ? "+IVA" : ""} | Saldo: ${p.balance.toLocaleString("es-AR")} | Tel: ${p.phone_price} | WA: ${p.wa_price}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditPlan({...p})} className="text-zinc-400 hover:text-white h-7 text-xs" data-testid={`edit-plan-${p.plan_id}`}>Editar</Button>
              <Button variant="ghost" size="sm" onClick={() => handleDeletePlan(p.plan_id)} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-7 w-7 p-0" data-testid={`delete-plan-${p.plan_id}`}><X className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
        {editPlan && (
          <div className="mt-4 p-4 bg-zinc-950 border border-violet-500/30 rounded-lg space-y-3" data-testid="plan-editor">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-zinc-500 block">ID</Label>
                <Input value={editPlan.plan_id} onChange={e => setEditPlan(p => ({...p, plan_id: e.target.value}))} placeholder="starter" disabled={plans.some(p => p.plan_id === editPlan.plan_id)} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Nombre</Label>
                <Input value={editPlan.name} onChange={e => setEditPlan(p => ({...p, name: e.target.value}))} placeholder="Starter" className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Precio base</Label>
                <Input type="number" value={editPlan.price} onChange={e => setEditPlan(p => ({...p, price: parseFloat(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Saldo</Label>
                <Input type="number" value={editPlan.balance} onChange={e => setEditPlan(p => ({...p, balance: parseFloat(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Descuento %</Label>
                <Input type="number" value={editPlan.discount_pct} onChange={e => setEditPlan(p => ({...p, discount_pct: parseFloat(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Orden</Label>
                <Input type="number" value={editPlan.order} onChange={e => setEditPlan(p => ({...p, order: parseInt(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Precio Tel.</Label>
                <Input type="number" step="0.01" value={editPlan.phone_price} onChange={e => setEditPlan(p => ({...p, phone_price: parseFloat(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500 block">Precio WA</Label>
                <Input type="number" step="0.01" value={editPlan.wa_price} onChange={e => setEditPlan(p => ({...p, wa_price: parseFloat(e.target.value) || 0}))} className="bg-zinc-900 border-zinc-800 text-white text-xs h-8" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editPlan.active} onChange={e => setEditPlan(p => ({...p, active: e.target.checked}))} className="rounded" />
                  <span className="text-xs text-zinc-400">Activo</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleSavePlan(editPlan)} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7" data-testid="save-plan-btn">Guardar Plan</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditPlan(null)} className="text-zinc-400 text-xs h-7">Cancelar</Button>
            </div>
          </div>
        )}
      </div>

      {/* UltraMsg Config */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <MessageCircle className="w-4 h-4 text-green-400" />UltraMsg (WhatsApp Mensajes)
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Instance ID</Label>
            <Input value={settings.ultramsg_instance} onChange={e => setSettings(s => ({ ...s, ultramsg_instance: e.target.value }))} placeholder="instanceXXXXX" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="ultramsg-instance" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Token</Label>
            <Input value={settings.ultramsg_token} onChange={e => setSettings(s => ({ ...s, ultramsg_token: e.target.value }))} placeholder="token..." type="password" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="ultramsg-token" />
          </div>
        </div>
      </div>

      {/* RapidAPI Config */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <Server className="w-4 h-4 text-blue-400" />RapidAPI (WhatsApp Verificacion)
        </h3>
        <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-zinc-950 border border-zinc-800">
          <div>
            <p className="text-sm font-medium text-white">Verificacion WA Automatica</p>
            <p className="text-xs text-zinc-500">Cuando esta activo, las campanas Tel+WA inician verificacion WA automaticamente al terminar</p>
          </div>
          <button
            onClick={() => setSettings(s => ({ ...s, wa_auto_trigger_enabled: !s.wa_auto_trigger_enabled }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${settings.wa_auto_trigger_enabled ? "bg-green-600" : "bg-zinc-700"}`}
            data-testid="wa-auto-trigger-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.wa_auto_trigger_enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">API Key</Label>
            <Input value={settings.rapidapi_key} onChange={e => setSettings(s => ({ ...s, rapidapi_key: e.target.value }))} placeholder="API Key..." type="password" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="rapidapi-key" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Host</Label>
            <Input value={settings.rapidapi_host} onChange={e => setSettings(s => ({ ...s, rapidapi_host: e.target.value }))} placeholder="bulk-whatsapp-validator.p.rapidapi.com" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" data-testid="rapidapi-host" />
          </div>
        </div>
      </div>

      {/* Notification Phones */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <Phone className="w-4 h-4 text-amber-400" />Telefonos de Notificacion
        </h3>
        <p className="text-xs text-zinc-500 mb-3">Los mensajes del formulario de contacto se enviaran por WhatsApp a estos numeros.</p>
        <div className="space-y-2 mb-3">
          {settings.notification_phones.map(phone => (
            <div key={phone} className="flex items-center gap-2 p-2 bg-zinc-950 border border-zinc-800 rounded-lg">
              <Phone className="w-3.5 h-3.5 text-green-400" />
              <span className="text-sm text-white font-mono flex-1">{phone}</span>
              <Button variant="ghost" size="sm" onClick={() => removePhone(phone)} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-7 w-7 p-0" data-testid={`remove-phone-${phone}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          {settings.notification_phones.length === 0 && (
            <p className="text-xs text-zinc-600 py-2">No hay telefonos configurados</p>
          )}
        </div>
        <div className="flex gap-2">
          <Input value={newPhone} onChange={e => setNewPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && addPhone()} placeholder="+549XXXXXXXXXX" className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm flex-1" data-testid="new-phone-input" />
          <Button onClick={addPhone} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-300" data-testid="add-phone-btn">
            <Plus className="w-4 h-4 mr-1" />Agregar
          </Button>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-settings-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Save className="w-4 h-4 mr-2" />Guardar Configuracion</>}
      </Button>
    </div>
  );
};
